import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import Breadcrumb from "@/components/ui/breadcrumb";
import Input from "@/components/ui/input";
import Textarea from "@/components/ui/textarea";
import Dropdown from "@/components/ui/dropdown";
import Button from "@/components/ui/button";
import Modal from "@/components/ui/modal";
import Switch from "@/components/ui/switch";
import FileInput from "@/components/ui/file-input";
import DatePicker from "@/components/ui/datepicker";
import { Plus, LeftChevron, Edit, Close, User } from "@/components/icons";
import DocumentUploadModal, {
  DocumentItem,
} from "../components/DocumentUploadModal";
import UnitAddModal, { UnitItem } from "../components/UnitAddModal";
import UnitContentEditor, {
  type UnitObjectiveItem,
} from "../components/UnitContentEditor";
import CreateLocationModal from "../components/CreateLocationModal";
import { generateEducationCode } from "../utils";
import { isLiveStreamQuestionsChapter } from "../utils/live-stream-questions";
import InstructorSelectModal, {
  InstructorItem,
} from "../components/InstructorSelectModal";
import { educationCatalogApi, EducationCategory } from "../services/education-catalog.api";
import { examManagementApi } from "@/features/exam-management/services/exam-management.api";
import { pollsApi } from "@/features/polls/services/polls.api";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import {
  EDUCATION_STATUS_API,
  EDUCATION_TYPE_API,
  SCOPE_TYPE_API,
  ObjectiveType,
  type AdminCreateEducationDto,
  type AdminPatchEducationDto,
  type CreateEducationChapterDto,
  type CreateObjectiveDto,
  type EducationDetailApi,
  type PresetLocationApi,
} from "@/features/education-management/types/education-management.types";
import { educationManagementApi } from "@/features/education-management/services/education-management.api";
import type { MandatoryHealthResponse } from "@/features/education-management/types/education-management.types";
import { approvalFlowsApi } from "@/features/approval-flows/services/approval-flows.api";
import { ApprovalTargetType } from "@/features/approval-flows/types/approval-flows.types";
import {
  certificatesApi,
  CertificateOwnerType,
} from "@/features/certificates/services/certificates.api";
import { getAdminUsers } from "@/lib/users.api";
import { useStore } from "@/store";
import { UserRoleType } from "@/features/roles-and-permissions/types/roles-and-permissions.types";
import ConfirmModal from "@/components/ui/confirm-modal";
import CompleteEducationModal from "../components/CompleteEducationModal";
import MapComponent from "@/components/ui/map";
import styles from "./CreateEducationPage.module.scss";
import { ALLOWED_FILE_TYPE_PRESETS } from "@/types/file-upload.types";
import { formatDurationFromParts } from "@/utils/format-duration";

interface FormData {
  /** 0 CENTRAL, 1 BRANCH, 2 ALL */
  targetAudience: number;
  educationFormat: string;
  educationCode: string;
  educationName: string;
  educationDescription: string;
  educationContent: string;
  coverImage: File | null;
  /** Backend'e yüklendikten sonra dönen dosya ID (kapak görseli) */
  coverImageFileId: string;
  /** Kapak görseli önizleme URL (object URL veya backend'den gelen) */
  coverImagePreview: string;
  documents: DocumentItem[];
  units: UnitItem[];
  /** Ünitesiz (NONUNIT) eğitimde tek implicit ünitedeki video/PDF objektifleri */
  nonUnitObjectives: UnitObjectiveItem[];
  nonUnitDurationHours?: number;
  nonUnitDurationMinutes?: number;
  // Right sidebar
  /**
   * Zorunlu eğitim bayrağı. true ise hedef kitledeki (scopeType + departments)
   * tüm kullanıcılara backend tarafından otomatik ENROLLED kaydı açılır; talep
   * toplama adımı atlanır. Form'da açıldığında RC toggle'ı disable edilir.
   */
  isMandatory: boolean;
  isCollectable: boolean;
  category: string;
  instructors: InstructorItem[];
  /** Eğitim Bazlı Yöneticiler — seçilen kişiler o eğitim için düzenleme
   *  yetkisine sahip olur (Madde 5). BE'ye `managerIDs: string[]` olarak gider. */
  managers: { id: string; name: string; email?: string }[];
  hasBudgetRequirement: boolean;
  budget: string;
  examDefinition: string;
  survey: string;
  certificate: string;
  /**
   * Bu eğitimin kayıt taleplerinin gireceği onay akışı (ApprovalFlow ID).
   * Boş bırakılırsa domain'in aktif eğitim kaydı akışı kullanılır.
   */
  approvalFlow: string;
  /** Eğitim başlama tarihi */
  startDate: Date | null;
  durationHours: string;
  durationMinutes: string;
  requestCollectionDateRange: [Date | null, Date | null];
  completionDate: Date | null;
  /** Maksimum katılımcı sayısı (max 100000) */
  capacity: string;
  /** Konum adresi (location) */
  location: string;
  /** Enlem – haritadan seçilebilir */
  latitude: string;
  /** Boylam – haritadan seçilebilir */
  longitude: string;
  presetLocationId: string;
  /** Ulaşım bilgisi */
  transportation: string;
}

export type { UnitItem };

/** Ortak alanlar (Taslak ve Yayınla için) – kapak görseli hariç */
const createEducationFormBaseSchema = z
  .object({
    educationName: z.string().min(1, "createEducation.validation.educationNameRequired"),
    educationDescription: z.string().min(1, "createEducation.validation.educationDescriptionRequired"),
    targetAudience: z
      .number({
        required_error: "createEducation.validation.targetAudienceRequired",
        invalid_type_error: "createEducation.validation.targetAudienceRequired",
      })
      .min(0, "createEducation.validation.targetAudienceRequired")
      .max(2, "createEducation.validation.invalidTargetAudience"),
    educationFormat: z.string().min(1, "createEducation.validation.educationFormatRequired"),
    // Backend (POST create + PATCH) sertifikayı zorunlu tutuyor; UI tarafında
    // da blokluyoruz ki 400 ile karşılaşmasınlar.
    certificate: z
      .string()
      .trim()
      .min(1, "createEducation.validation.certificateRequired")
      .uuid("createEducation.validation.certificateUuid"),
    coverImageFileId: z.string().optional().default(""),
    isMandatory: z.boolean().optional().default(false),
    isCollectable: z.boolean().optional().default(false),
    startDate: z.date().nullable(),
    completionDate: z.date().nullable(),
    requestCollectionDateRange: z.tuple([
      z.date().nullable(),
      z.date().nullable(),
    ]),
    units: z.array(
      z.object({
        name: z.string(),
        id: z.string().optional(),
        fileIDs: z.array(z.string()).optional(),
        objectives: z
          .array(
            z.object({
              id: z.string().optional(),
              fileID: z.string(),
              videoQuestions: z
                .array(
                  z.object({
                    id: z.string().optional(),
                    questionText: z.string(),
                    timestamp: z.string(),
                    options: z.array(
                      z.object({
                        id: z.string().optional(),
                        optionText: z.string(),
                        isCorrect: z.boolean(),
                      }),
                    ),
                  }),
                )
                .optional(),
            }),
          )
          .optional(),
      }),
    ),
    nonUnitObjectives: z
      .array(
        z.object({
          id: z.string(),
          fileID: z.string(),
        }),
      )
      .default([]),
  })
  .refine(
    (data) => {
      if (!data.isCollectable) return true;
      return (
        data.requestCollectionDateRange[0] != null &&
        data.requestCollectionDateRange[1] != null
      );
    },
    {
      message: "createEducation.validation.requestDatesRequired",
      path: ["requestCollectionDateRange"],
    },
  )
  .refine(
    (data) => {
      if (data.educationFormat !== "unit-based") return true;
      return (
        data.units.length > 0 &&
        data.units.every((u) => u.name.trim().length > 0)
      );
    },
    {
      message: "createEducation.validation.unitsRequired",
      path: ["units"],
    },
  )
  .refine(
    (data) => {
      if (data.educationFormat !== "non-unit-based") return true;
      return data.nonUnitObjectives.length > 0;
    },
    {
      message: "createEducation.validation.nonUnitObjectivesRequired",
      path: ["nonUnitObjectives"],
    },
  )
  .refine(
    (data) => {
      if (data.startDate == null || data.completionDate == null) return true;
      return data.completionDate >= data.startDate;
    },
    {
      message: "createEducation.validation.endBeforeStart",
      path: ["completionDate"],
    },
  )
  .refine(
    (data) => {
      const [start, end] = data.requestCollectionDateRange;
      if (start == null || end == null) return true;
      return start <= end;
    },
    {
      message: "createEducation.validation.requestEndBeforeStart",
      path: ["requestCollectionDateRange"],
    },
  )
  .refine(
    (data) => {
      // Talep toplama bitişi eğitim başlangıcından SONRA olamaz (gün bazlı:
      // aynı gün serbest). Ham Date kıyası düzenleme sayfasında 23:59 bitiş ile
      // 00:00 başlangıcı karşılaştırıp aynı günü reddediyordu.
      const [, reqEnd] = data.requestCollectionDateRange;
      if (reqEnd == null || data.startDate == null) return true;
      const day = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
      return day(reqEnd) <= day(data.startDate);
    },
    {
      message:
        "createEducation.validation.requestEndAfterEduStart",
      path: ["requestCollectionDateRange"],
    },
  )
  .refine(
    (data) => {
      // Tek taraflı talep toplama aralığı kabul edilmez (isCollectable açıksa ikisi de dolu olmalı, bu üstte kontrol ediliyor;
      // kapalı ise ikisi de null olmalı)
      if (data.isCollectable) return true;
      const [s, e] = data.requestCollectionDateRange;
      return s == null && e == null;
    },
    {
      message:
        "createEducation.validation.requestClosedDatesEmpty",
      path: ["requestCollectionDateRange"],
    },
  );

interface ValidateContext {
  /** Mevcut başvurulmuş/kayıtlı kullanıcı sayısı – kapasite bunun altına düşmemeli */
  enrolledCount?: number;
  /** Yayınlama akışı (Taslak Kaydet değil) */
  isPublishing: boolean;
  /** Eğitim şu anda zaten yayında/aktif mi (edit mode) */
  isAlreadyPublished: boolean;
}

function validateCreateEducationForm(
  formData: FormData,
  ctx: ValidateContext = { isPublishing: false, isAlreadyPublished: false },
  t: (k: string, opts?: Record<string, unknown>) => string = (k) => k,
):
  | { success: true }
  | { success: false; errors: string[]; fieldErrors: Record<string, string> } {
  const toValidate = {
    educationName: formData.educationName,
    educationDescription: formData.educationDescription,
    targetAudience: formData.targetAudience,
    educationFormat: formData.educationFormat,
    certificate: formData.certificate,
    coverImageFileId: formData.coverImageFileId,
    isMandatory: formData.isMandatory,
    isCollectable: formData.isCollectable,
    startDate: formData.startDate,
    completionDate: formData.completionDate,
    requestCollectionDateRange: formData.requestCollectionDateRange,
    units: formData.units,
    nonUnitObjectives: formData.nonUnitObjectives.map((o) => ({
      id: o.id,
      fileID: o.fileID,
    })),
  };
  const result = createEducationFormBaseSchema.safeParse(toValidate);
  const messages: string[] = [];
  const fieldErrors: Record<string, string> = {};
  if (!result.success) {
    const errors = result.error.flatten();
    messages.push(
      ...(errors.formErrors ?? []),
      ...Object.values(errors.fieldErrors)
        .flat()
        .filter((m): m is string => typeof m === "string"),
    );
    Object.entries(errors.fieldErrors ?? {}).forEach(([key, val]) => {
      const first = Array.isArray(val) ? val[0] : val;
      if (typeof first === "string") {
        fieldErrors[key] = /^required$/i.test(first.trim())
          ? "Bu alan zorunludur"
          : first;
      }
    });
    // Toast mesajlarında da ham "Required" sızmasın
    for (let i = 0; i < messages.length; i++) {
      if (/^required$/i.test(messages[i].trim())) {
        messages[i] = "Bu alan zorunludur";
      }
    }
  }

  const durationMinutes = parseOptionalNumber(formData.durationMinutes);
  if (
    durationMinutes != null &&
    (durationMinutes < 0 || durationMinutes > 59)
  ) {
    fieldErrors.durationMinutes =
      t("createEducation.validation.durationMinutesRange");
    messages.push(fieldErrors.durationMinutes);
  }
  const durationHours = parseOptionalNumber(formData.durationHours);
  if (durationHours != null && durationHours < 0) {
    fieldErrors.durationHours = t("createEducation.validation.durationHoursMin");
    messages.push(fieldErrors.durationHours);
  }

  formData.units.forEach((unit, index) => {
    const unitMinutes =
      unit.durationMinutes != null ? Number(unit.durationMinutes) : undefined;
    if (
      unitMinutes != null &&
      (Number.isNaN(unitMinutes) || unitMinutes < 0 || unitMinutes > 59)
    ) {
      messages.push(t("createEducation.errors.unitMinutesRange", { index: index + 1 }));
    }
    const unitHours =
      unit.durationHours != null ? Number(unit.durationHours) : undefined;
    if (unitHours != null && (Number.isNaN(unitHours) || unitHours < 0)) {
      messages.push(`Ünite ${index + 1}: Saat 0'dan küçük olamaz.`);
    }
  });

  const totalHours = parseOptionalNumber(formData.durationHours);
  const totalMinutes = parseOptionalNumber(formData.durationMinutes) ?? 0;
  const totalProvided = totalHours != null || totalMinutes > 0;
  const unitDurations = formData.units.map((u) => ({
    hours: u.durationHours,
    minutes: u.durationMinutes,
  }));
  const unitDurationProvided = unitDurations.some(
    (u) => u.hours != null || u.minutes != null,
  );
  if (totalProvided && unitDurationProvided) {
    const total = getTotalMinutes(totalHours ?? 0, totalMinutes);
    const unitTotal = unitDurations.reduce((acc, cur) => {
      const h = cur.hours != null ? Number(cur.hours) : 0;
      const m = cur.minutes != null ? Number(cur.minutes) : 0;
      return acc + getTotalMinutes(h, m);
    }, 0);
    if (total !== unitTotal) {
      fieldErrors.durationTotal =
        t("createEducation.validation.totalDurationMatch");
      messages.push(fieldErrors.durationTotal);
    }
  }

  // Kapasite mevcut kayıt sayısının altına düşürülemez
  if (
    ctx.enrolledCount != null &&
    ctx.enrolledCount > 0 &&
    formData.capacity.trim()
  ) {
    const n = Number(formData.capacity);
    if (!Number.isNaN(n) && n < ctx.enrolledCount) {
      fieldErrors.capacity = t("createEducation.errors.capacityBelowEnrolled", { count: ctx.enrolledCount });
      messages.push(fieldErrors.capacity);
    }
  }

  // İlk yayın akışında (taslak -> yayın veya yeni eğitim) başlangıç tarihi geçmişte olamaz
  if (
    ctx.isPublishing &&
    !ctx.isAlreadyPublished &&
    formData.startDate != null
  ) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDay = new Date(
      formData.startDate.getFullYear(),
      formData.startDate.getMonth(),
      formData.startDate.getDate(),
    );
    if (startDay < today) {
      fieldErrors.startDate = t("createEducation.validation.startDatePast");
      messages.push(fieldErrors.startDate);
    }
  }

  if (messages.length === 0) return { success: true };
  return { success: false, errors: messages, fieldErrors };
}

/** Tarihi yerel gün başına (00:00:00) çekip ISO string döner */
function toStartOfDayISO(d: Date | null): string | undefined {
  if (!d || Number.isNaN(d.getTime())) return undefined;
  const start = new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
    0,
    0,
    0,
    0,
  );
  return start.toISOString();
}

/** Tarihi yerel gün sonuna (23:59:59.999) çekip ISO string döner */
function toEndOfDayISO(d: Date | null): string | undefined {
  if (!d || Number.isNaN(d.getTime())) return undefined;
  const end = new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
    23,
    59,
    59,
    999,
  );
  return end.toISOString();
}

const parseOptionalNumber = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  if (Number.isNaN(n)) return undefined;
  return n;
};

const getTotalMinutes = (hours?: number, minutes?: number) => {
  const h = typeof hours === "number" && !Number.isNaN(hours) ? hours : 0;
  const m = typeof minutes === "number" && !Number.isNaN(minutes) ? minutes : 0;
  return h * 60 + m;
};

const formatDurationLabel = (hours?: number, minutes?: number) => {
  const label = formatDurationFromParts(hours, minutes);
  return label === "-" ? "" : label;
};

/** Ünite bölümleri + isteğe bağlı canlı yayın soruları chapter'ı */
function mergeEducationChapters(
  formData: FormData,
): CreateEducationChapterDto[] | undefined {
  if (formData.educationFormat === "non-unit-based") {
    if (formData.nonUnitObjectives.length === 0) return undefined;
    const educationName = formData.educationName.trim();
    const chapterTitle = (educationName || "İçerik").slice(0, 200);
    return [
      {
        title: chapterTitle,
        order: 0,
        durationHours: formData.nonUnitDurationHours,
        durationMinutes: formData.nonUnitDurationMinutes,
        objectives: formData.nonUnitObjectives.map(
          (obj, objIdx): CreateObjectiveDto => {
            const cleanedQuestions = (obj.videoQuestions ?? [])
              .map((q, qIdx) => {
                const questionText = (q.questionText ?? "").trim();
                const timestamp = (q.timestamp ?? "").trim();
                const options = (q.options ?? [])
                  .map((o, optIdx) => ({
                    optionText: (o.optionText ?? "").trim(),
                    order: optIdx,
                    isCorrect: !!o.isCorrect,
                  }))
                  .filter((o) => o.optionText.length > 0);
                if (!questionText || !timestamp || options.length < 2)
                  return null;
                return { questionText, timestamp, order: qIdx, options };
              })
              .filter((q): q is NonNullable<typeof q> => q !== null);
            const baseTitle = educationName || "İçerik";
            const titleSlice = baseTitle.slice(0, 180);
            return {
              type: ObjectiveType.INTERNAL_FILE,
              title: `${titleSlice}${baseTitle.length > 180 ? "…" : ""} - ${objIdx + 1}`,
              fileID: obj.fileID,
              order: objIdx,
              isVisibleToUser: true,
              videoQuestions:
                cleanedQuestions.length > 0 ? cleanedQuestions : undefined,
            };
          },
        ),
      },
    ];
  }

  const unitChapters: CreateEducationChapterDto[] =
    formData.educationFormat === "unit-based" && formData.units.length > 0
      ? formData.units.map(
          (unit, chIdx): CreateEducationChapterDto => ({
            title: unit.name.trim().slice(0, 200),
            order: chIdx,
            durationHours:
              unit.durationHours != null
                ? Number(unit.durationHours)
                : undefined,
            durationMinutes:
              unit.durationMinutes != null
                ? Number(unit.durationMinutes)
                : undefined,
            // objectives tanımlıysa (boş bile olsa) onu kullan; sadece TAMAMEN
            // undefined ise fileIDs'e fallback. Önceki `?.length` kontrolü,
            // kullanıcı modalde tüm dosyaları kaldırıp kaydettiğinde parent'a
            // boş objectives gelmesine rağmen eski fileIDs'i yayına basıyordu.
            objectives: (unit.objectives !== undefined
              ? unit.objectives
              : (unit.fileIDs ?? []).map((fileID, i) => ({
                  id: `obj-${i}-${fileID}`,
                  fileID,
                  videoQuestions: undefined,
                }))
            ).map((obj, objIdx): CreateObjectiveDto => {
              const cleanedQuestions = (obj.videoQuestions ?? [])
                .map((q, qIdx) => {
                  const questionText = (q.questionText ?? "").trim();
                  const timestamp = (q.timestamp ?? "").trim();
                  const options = (q.options ?? [])
                    .map((o, optIdx) => ({
                      optionText: (o.optionText ?? "").trim(),
                      order: optIdx,
                      isCorrect: !!o.isCorrect,
                    }))
                    .filter((o) => o.optionText.length > 0);
                  if (!questionText || !timestamp || options.length < 2)
                    return null;
                  return {
                    questionText,
                    timestamp,
                    order: qIdx,
                    options,
                  };
                })
                .filter(
                  (q): q is NonNullable<typeof q> => q !== null,
                );
              return {
                type: ObjectiveType.INTERNAL_FILE,
                title: `${unit.name.trim().slice(0, 180)}${unit.name.length > 180 ? "…" : ""} - ${objIdx + 1}`,
                fileID: obj.fileID,
                order: objIdx,
                isVisibleToUser: true,
                videoQuestions:
                  cleanedQuestions.length > 0 ? cleanedQuestions : undefined,
              };
            }),
          }),
        )
      : [];

  const merged: CreateEducationChapterDto[] = [
    ...unitChapters,
  ];
  if (merged.length === 0) return undefined;
  merged.forEach((ch, i) => {
    ch.order = i;
  });
  return merged;
}

const typeToFormat: Record<number, string> = {
  [EDUCATION_TYPE_API.ON_SITE]: "face-to-face",
  [EDUCATION_TYPE_API.ONLINE]: "online",
  [EDUCATION_TYPE_API.UNIT]: "unit-based",
  [EDUCATION_TYPE_API.NONUNIT]: "non-unit-based",
};
const CreateEducationPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { educationId } = useParams<{ educationId: string }>();
  const isEditMode = !!educationId;

  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  // Düzenleme modunda ilk boyama boş "oluştur" formu göstermesin: veri gelene
  // kadar form kilitli kalır (aksi halde boş formla Kaydet alanları siliyordu).
  const [isLoadingDetail, setIsLoadingDetail] = useState(isEditMode);
  const [detailApplied, setDetailApplied] = useState(false);
  const [editingDocument, setEditingDocument] = useState<DocumentItem | null>(
    null,
  );
  const [isUnitModalOpen, setIsUnitModalOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState<UnitItem | null>(null);
  // Eğitim soruları (liveStreamQuestions) ayrı bölüm olarak kaldırıldı.
  const [isInstructorModalOpen, setIsInstructorModalOpen] = useState(false);

  // Dropdown options from backend APIs
  const [categoryOptions, setCategoryOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [examOptions, setExamOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [surveyOptions, setSurveyOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [certificateOptions, setCertificateOptions] = useState<
    { label: string; value: string }[]
  >([]);
  /** Aktif eğitim kaydı onay akışları — eğitim başına seçim için. */
  /** Sistemin onay akışı — akış seçme yetkisi olmayanlara bilgi olarak gösterilir. */
  const [systemApprovalFlow, setSystemApprovalFlow] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [systemApprovalFlowLoaded, setSystemApprovalFlowLoaded] = useState(false);
  const [approvalFlowOptions, setApprovalFlowOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [instructorList, setInstructorList] = useState<InstructorItem[]>([]);
  /** Eğitim Bazlı Yönetici dropdown'u için kullanıcı listesi.
   *  getAdminUsers üzerinden search ile doldurulur. */
  const [managerOptions, setManagerOptions] = useState<
    { label: string; value: string; email?: string }[]
  >([]);
  const [isLoadingManagers, setIsLoadingManagers] = useState(false);
  const managerSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [presetLocations, setPresetLocations] = useState<PresetLocationApi[]>(
    [],
  );
  const [initialPresetLocationId, setInitialPresetLocationId] = useState("");
  /**
   * Konum giriş modu:
   * - "PRESET": hazır konum dropdown'undan seçilir, adres/lat/lng readonly preview
   * - "MANUAL": kullanıcı adres + harita üzerinden serbestçe girer
   * Create modunda default MANUAL. Edit modunda detay yüklendiğinde
   * presetLocationId'ye göre hesaplanır.
   */
  const [locationMode, setLocationMode] = useState<"PRESET" | "MANUAL">(
    "MANUAL",
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isCoverUploading, setIsCoverUploading] = useState(false);
  /** Detaydan: bütçe onayı bekliyor mu – buton görünürlüğü için */
  const [
    educationIsWaitingBudgetApproval,
    setEducationIsWaitingBudgetApproval,
  ] = useState(false);
  /** Detaydan: bütçe onaylandı mı – switch disable için */
  const [educationIsBudgetApproved, setEducationIsBudgetApproved] =
    useState(false);
  const [isApprovingBudget, setIsApprovingBudget] = useState(false);
  const { hasPermission } = usePermission();
  const canViewLocations = hasPermission(PERMISSIONS.LOCATIONS_VIEW);
  // Akış listesi APPROVAL_FLOWS yetkisi istiyor. Yetkisi olmayan (tipik olarak
  // Eğitim Yöneticisi) akış seçemez; onun yerine Sistem Yöneticisinin
  // belirlediği "sistemin onay akışı" salt-okunur gösterilir.
  const canChooseApprovalFlow = hasPermission(PERMISSIONS.APPROVAL_FLOWS_PAGE);
  const userType = useStore((s) => s.userType);
  const isEducationManager = userType === UserRoleType.EDUCATION_MANAGER;
  // Eğitim Sorumlusu (atanmış manager olabilir, ama yetki yükseltememeli):
  // backend `educationManagerIds` alanını strict SUPERVISOR'dan reddediyor.
  // UI tarafında da "Yönetici Seç" alanını disable ederek görsel feedback ver.
  const isStrictEducationSupervisorUser =
    userType === UserRoleType.EDUCATION_SUPERVISOR;
  const [educationApiStatus, setEducationApiStatus] = useState<number | null>(null);
  /** Detaydan: mevcut başvuru/kayıt sayısı – kapasite buna göre zorlanır */
  const [enrolledCount, setEnrolledCount] = useState<number>(0);
  const [isNewPeriodModalOpen, setIsNewPeriodModalOpen] = useState(false);
  const [isStartingNewPeriod, setIsStartingNewPeriod] = useState(false);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const [isStartModalOpen, setIsStartModalOpen] = useState(false);
  const [isStartingEducation, setIsStartingEducation] = useState(false);
  const [isCreateLocationOpen, setIsCreateLocationOpen] = useState(false);
  // Zorunlu eğitim atama sağlık durumu (koşullu reconcile banner'ı).
  // Yalnızca isRequired=true + edit modda fetch edilir; isHealthy=false iken
  // "Atamayı Yeniden Çalıştır" butonu görünür.
  const [mandatoryHealth, setMandatoryHealth] =
    useState<MandatoryHealthResponse | null>(null);
  const [isReconciling, setIsReconciling] = useState(false);
  // "Eksik atamalar" modal state'i — banner üzerinden açılır.
  const [isMissingUsersOpen, setIsMissingUsersOpen] = useState(false);
  const [missingUsers, setMissingUsers] = useState<
    Array<{
      id: string;
      firstName: string | null;
      lastName: string | null;
      email: string | null;
      phoneNumber: string | null;
      departmentID: string | null;
      departmentName: string | null;
    }>
  >([]);
  const [isLoadingMissingUsers, setIsLoadingMissingUsers] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    targetAudience: SCOPE_TYPE_API.ALL,
    educationFormat: "",
    educationCode: "",
    educationName: "",
    educationDescription: "",
    educationContent: "",
    coverImage: null,
    coverImageFileId: "",
    coverImagePreview: "",
    documents: [],
    units: [],
    nonUnitObjectives: [],
    isMandatory: false,
    isCollectable: true,
    category: "",
    instructors: [],
    managers: [],
    hasBudgetRequirement: false,
    budget: "",
    examDefinition: "",
    survey: "",
    certificate: "",
    approvalFlow: "",
    startDate: null,
    durationHours: "",
    durationMinutes: "",
    requestCollectionDateRange: [null, null],
    completionDate: null,
    capacity: "",
    location: "",
    latitude: "",
    longitude: "",
    presetLocationId: "",
    transportation: "",
  });

  const [templatePrefillModalOpen, setTemplatePrefillModalOpen] = useState(false);
  const [templatePrefillData, setTemplatePrefillData] = useState<null | {
    name: string;
    description: string | null;
    content: string | null;
    durationMinutes: number;
    type: number;
    scopeType: number;
    categoryIDs: string[];
  }>(null);
  const [isLookingUpTemplate, setIsLookingUpTemplate] = useState(false);
  const lastLookedUpCodeRef = useRef<string>("");

  // Fetch categories as tree and flatten with indentation so parent/child breakdown is visible in dropdown
  useEffect(() => {
    const flatten = (
      nodes: EducationCategory[],
      depth: number,
      acc: { label: string; value: string }[],
    ) => {
      for (const node of nodes) {
        const name = node.name ?? node.label ?? node.id;
        const prefix = depth === 0 ? "" : `${"\u00A0\u00A0".repeat(depth)}↳ `;
        acc.push({ label: `${prefix}${name}`, value: node.id });
        if (node.subCategories && node.subCategories.length > 0) {
          flatten(node.subCategories, depth + 1, acc);
        }
      }
      return acc;
    };
    educationCatalogApi
      .getEducationCategoriesTree(true)
      .then((tree) => {
        setCategoryOptions(flatten(tree, 0, []));
      })
      .catch(() => setCategoryOptions([]));
  }, []);

  // Fetch exams (GET /api/admin/exams) — hem atanmış hem atanmamış sınavlar
  // dropdown'da görünmeli; status filtresi kaldırıldı.
  useEffect(() => {
    examManagementApi
      .getAll({ page: 1, pageSize: 100 })
      .then((res) => {
        setExamOptions(res.data.map((e) => ({ label: e.name, value: e.id })));
      })
      .catch(() => setExamOptions([]));
  }, []);

  // Fetch surveys (GET /api/admin/surveys) — atanmış + atanmamış birlikte listelenir.
  useEffect(() => {
    pollsApi
      .getAll({ page: 1, pageSize: 100 })
      .then((res) => {
        setSurveyOptions(
          res.data.map((p) => ({ label: p.pollName, value: p.id })),
        );
      })
      .catch(() => setSurveyOptions([]));
  }, []);

  // Fetch certificates (GET /api/admin/certificates)
  // Yalnızca eğitim sertifikaları: staj sertifikası bir eğitime atanamaz,
  // listede görünmesi yanlış seçime davetiye çıkarırdı.
  useEffect(() => {
    certificatesApi
      .getAdminCertificates({
        page: 1,
        limit: 100,
        ownerType: CertificateOwnerType.EDUCATION,
      })
      .then((res) => {
        setCertificateOptions(
          res.data.map((c) => ({ label: c.title ?? c.id, value: c.id })),
        );
      })
      .catch(() => setCertificateOptions([]));
  }, []);

  // Onay akışları (GET /api/admin/approval-flows) — yalnız AKTİF ve eğitim
  // kaydı türündekiler. Backend seçimi de aynı iki koşulla doğruluyor.
  //
  // Bu uç APPROVAL_FLOWS.PAGE + VIEW istiyor; yetkisi olmayanda 403 dönüp liste
  // sessizce boşalıyordu ("onay akışları listede çıkmıyor"). Artık istek
  // yalnızca yetkiliyken atılıyor, yetkisiz kullanıcı aşağıdaki sistem akışı
  // uçlarından bilgilendiriliyor.
  useEffect(() => {
    if (!canChooseApprovalFlow) {
      setApprovalFlowOptions([]);
      return;
    }
    approvalFlowsApi
      .getAll({
        page: 1,
        limit: 100,
        targetType: ApprovalTargetType.EDUCATION_ENROLLMENT,
        isActive: true,
      })
      .then((res) => {
        setApprovalFlowOptions(
          res.data.map((f) => ({
            label: f.isDefault
              ? `${f.title ?? f.id} (sistemin akışı)`
              : (f.title ?? f.id),
            value: f.id,
          })),
        );
      })
      .catch(() => setApprovalFlowOptions([]));
  }, [canChooseApprovalFlow]);

  // Sistemin onay akışı (GET /api/admin/educations/approval-flow) — EDUCATIONS.VIEW
  // ile okunur, akış tasarımı yetkisi gerektirmez. Hem yetkisiz kullanıcının
  // salt-okunur alanını hem yetkilinin "boş bırakırsan bu çalışır" ipucunu besler.
  useEffect(() => {
    educationManagementApi
      .getSystemApprovalFlow()
      .then((flow) =>
        setSystemApprovalFlow(flow ? { id: flow.id, title: flow.title } : null),
      )
      .catch(() => setSystemApprovalFlow(null))
      .finally(() => setSystemApprovalFlowLoaded(true));
  }, []);

  // ---------------------------------------------------------------------------
  // Edit modunda seçili öğeyi listede garanti et ("ensure-selected option").
  // Dropdown listeleri sayfalı/limitli çekildiği için, eğitime atanmış öğe
  // limit dışında kalırsa selectedLabel bulunamayıp placeholder gösteriliyordu.
  // Aşağıdaki effect'ler: seçili ID dolu AMA options'ta yoksa by-id ile çekip
  // listeye ekler. Öğe zaten listedeyse istek atılmaz; ref ile tekrar fetch
  // engellenir.
  // ---------------------------------------------------------------------------
  const ensuredSurveyRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const id = formData.survey;
    if (!id || ensuredSurveyRef.current.has(id)) return;
    if (surveyOptions.some((o) => o.value === id)) return;
    ensuredSurveyRef.current.add(id);
    pollsApi
      .getById(id)
      .then((p) =>
        setSurveyOptions((prev) =>
          prev.some((o) => o.value === id)
            ? prev
            : [{ label: p.pollName, value: p.id }, ...prev],
        ),
      )
      .catch(() => {
        /* seçili anket çekilemezse sessizce yoksay */
      });
  }, [formData.survey, surveyOptions]);

  const ensuredCertificateRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const id = formData.certificate;
    if (!id || ensuredCertificateRef.current.has(id)) return;
    if (certificateOptions.some((o) => o.value === id)) return;
    ensuredCertificateRef.current.add(id);
    certificatesApi
      .getCertificateDetail(id)
      .then((detail) => {
        if (!detail) return;
        setCertificateOptions((prev) =>
          prev.some((o) => o.value === detail.id)
            ? prev
            : [{ label: detail.name ?? detail.id, value: detail.id }, ...prev],
        );
      })
      .catch(() => {
        /* seçili sertifika çekilemezse sessizce yoksay */
      });
  }, [formData.certificate, certificateOptions]);

  const ensuredExamRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const id = formData.examDefinition;
    if (!id || ensuredExamRef.current.has(id)) return;
    if (examOptions.some((o) => o.value === id)) return;
    ensuredExamRef.current.add(id);
    examManagementApi
      .getById(id)
      .then((exam) =>
        setExamOptions((prev) =>
          prev.some((o) => o.value === exam.id)
            ? prev
            : [{ label: exam.name, value: exam.id }, ...prev],
        ),
      )
      .catch(() => {
        /* seçili sınav çekilemezse sessizce yoksay */
      });
  }, [formData.examDefinition, examOptions]);

  // Hazır konum: backend'de by-id GET yok; ancak eğitim detayı presetLocationId
  // + adres + koordinat döndürdüğü için opsiyonu lokal olarak sentezliyoruz.
  useEffect(() => {
    const id = formData.presetLocationId;
    if (!id) return;
    if (presetLocations.some((l) => l.id === id)) return;
    setPresetLocations((prev) =>
      prev.some((l) => l.id === id)
        ? prev
        : [
            {
              id,
              name: formData.location || id,
              address: formData.location || "",
              latitude: formData.latitude ? Number(formData.latitude) : null,
              longitude: formData.longitude ? Number(formData.longitude) : null,
            },
            ...prev,
          ],
    );
  }, [
    formData.presetLocationId,
    presetLocations,
    formData.location,
    formData.latitude,
    formData.longitude,
  ]);

  // Fetch instructors (GET /api/users/instructors)
  useEffect(() => {
    educationCatalogApi
      .getInstructors()
      .then((list) => {
        const items: InstructorItem[] = list.map((i) => {
          const id = (i as { userID?: string }).userID ?? i.id;
          const name =
            [i.firstName, i.lastName].filter(Boolean).join(" ").trim() || id;
          const tcKimlikNo =
            (i as { tcKimlikNo?: string; identityNumber?: string })
              .tcKimlikNo ??
            (i as { tcKimlikNo?: string; identityNumber?: string })
              .identityNumber ??
            "";
          return { id, name, tcKimlikNo };
        });
        setInstructorList(items);
      })
      .catch(() => setInstructorList([]));
  }, []);

  // Eğitim Bazlı Yönetici (Madde 5) — admin kullanıcılarını listele
  const fetchManagerUsers = useCallback(
    async (
      search: string,
      preserve?: { id: string; name: string; email?: string }[],
    ) => {
      setIsLoadingManagers(true);
      try {
        const { data } = await getAdminUsers({
          page: 1,
          limit: 20,
          search: search.trim() || undefined,
          sortBy: "firstName",
          sortOrder: "asc",
          roleType: UserRoleType.EDUCATION_SUPERVISOR,
        });
        const options = data.map((u) => {
          const fullName =
            [u.firstName, u.lastName].filter(Boolean).join(" ") ||
            u.email ||
            u.id;
          return { label: fullName, value: u.id, email: u.email };
        });
        // Halihazırda seçili olup sonuçta yoksa listeye ekle (chip'in doğru
        // görünmesi için)
        const merged = [...options];
        (preserve ?? []).forEach((p) => {
          if (!merged.some((o) => o.value === p.id)) {
            merged.unshift({
              label: p.name,
              value: p.id,
              email: p.email,
            });
          }
        });
        setManagerOptions(merged);
      } catch {
        setManagerOptions(
          (preserve ?? []).map((p) => ({
            label: p.name,
            value: p.id,
            email: p.email,
          })),
        );
      } finally {
        setIsLoadingManagers(false);
      }
    },
    [],
  );

  // İlk yükleme
  useEffect(() => {
    fetchManagerUsers("");
  }, [fetchManagerUsers]);

  const handleManagerSearch = (value: string) => {
    if (managerSearchDebounceRef.current)
      clearTimeout(managerSearchDebounceRef.current);
    managerSearchDebounceRef.current = setTimeout(() => {
      fetchManagerUsers(value, formData.managers);
      managerSearchDebounceRef.current = null;
    }, 300);
  };

  const handleManagerToggle = (value: string | string[]) => {
    const ids = Array.isArray(value) ? value : value ? [value] : [];
    setFormData((prev) => {
      // Daha önce seçilip name'i biliniyorsa onu kullan, yoksa option'dan al
      const prevById = new Map(prev.managers.map((m) => [m.id, m]));
      const newManagers = ids.map((id) => {
        const existing = prevById.get(id);
        if (existing) return existing;
        const opt = managerOptions.find((o) => o.value === id);
        return {
          id,
          name: opt?.label ?? id,
          email: opt?.email,
        };
      });
      return { ...prev, managers: newManagers };
    });
  };

  useEffect(() => {
    if (!canViewLocations) return;
    educationManagementApi
      .getLocations({ page: 1, limit: 50 })
      .then((res) => {
        const list = res?.data ?? [];
        setPresetLocations(
          list.filter((l) => l.latitude != null && l.longitude != null),
        );
      })
      .catch(() => setPresetLocations([]));
  }, [canViewLocations]);

  const setFormFromDetail = useCallback(
    (d: EducationDetailApi) => {
      const isRequiredEducation = d.isRequired === true;
      // Kullanıcı niyetini runtime flag tek başına yansıtmıyor: BUDGET_PENDING'de
      // backend `isAcceptingRequestCollection=false` döner ama tarihleri saklı
      // tutar. Tarihler varsa kullanıcı RC istemiş demektir; toggle'ı kapalı
      // yüklersek bütçe onayı sonrası kaydette eğitim ACTIVE'e atılır ve
      // kursiyere kapanır.
      const hasStoredRequestCollectionDates =
        d.requestCollectionStartDate != null ||
        d.requestCollectionEndDate != null;
      const intentIsCollectable =
        !isRequiredEducation &&
        ((d.isAcceptingRequestCollection ?? true) ||
          hasStoredRequestCollectionDates);
      const [reqStart, reqEnd] = intentIsCollectable
        ? [
            d.requestCollectionStartDate
              ? new Date(d.requestCollectionStartDate)
              : null,
            d.requestCollectionEndDate
              ? new Date(d.requestCollectionEndDate)
              : null,
          ]
        : [null, null];

      // Sadece Üniteli (UNIT) eğitimlerde chapter'ları unitlere map'le (canlı soru bölümü hariç)
      const isUnitBasedType = d.type === EDUCATION_TYPE_API.UNIT;
      const isNonUnitBasedType = d.type === EDUCATION_TYPE_API.NONUNIT;
      const rawChapters = Array.isArray(d.chapters) ? d.chapters : [];
      const unitChaptersOnly = rawChapters.filter(
        (ch) => !isLiveStreamQuestionsChapter(ch),
      );
      const units: UnitItem[] =
        isUnitBasedType && unitChaptersOnly.length > 0
          ? unitChaptersOnly.map((ch, i) => ({
              id: (ch as { id?: string }).id ?? `unit-${i}-${Date.now()}`,
              name: ch.title,
              durationHours:
                (ch as { durationHours?: number }).durationHours ?? undefined,
              durationMinutes:
                (ch as { durationMinutes?: number }).durationMinutes ??
                undefined,
              objectives:
                (ch.objectives ?? [])
                  .map((o, objIdx) => {
                    const fileID = o.fileID;
                    if (!fileID) return null;
                    return {
                      id: o.id ?? `obj-${objIdx}-${fileID}`,
                      fileID,
                      videoQuestions:
                        (o.videoQuestions ?? [])
                          .map((q, qIdx) => ({
                            id: `q-${qIdx}-${Date.now()}`,
                            questionText: q.questionText ?? "",
                            timestamp: q.timestamp ?? "00:00:00",
                            options:
                              (q.options ?? []).map((opt, optIdx) => ({
                                id: opt.id ?? `opt-${optIdx}-${Date.now()}`,
                                optionText: opt.optionText ?? "",
                                isCorrect: !!opt.isCorrect,
                              })) ?? [],
                          }))
                          .filter((q) => (q.options?.length ?? 0) > 0) ?? [],
                    };
                  })
                  .filter((x): x is NonNullable<typeof x> => !!x) ?? [],
              fileIDs:
                (ch.objectives ?? [])
                  .map((o) => o.fileID)
                  .filter((id): id is string => !!id) ?? [],
            }))
          : [];
      // Ünitesiz (NONUNIT) — backend tek bir chapter saklar; objektifleri inline editör için aç.
      const nonUnitFirstChapter =
        isNonUnitBasedType ? unitChaptersOnly[0] : undefined;
      const nonUnitObjectives: UnitObjectiveItem[] = nonUnitFirstChapter
        ? (nonUnitFirstChapter.objectives ?? [])
            .map((o, objIdx) => {
              const fileID = o.fileID;
              if (!fileID) return null;
              return {
                id: o.id ?? `obj-${objIdx}-${fileID}`,
                fileID,
                videoQuestions:
                  (o.videoQuestions ?? [])
                    .map((q, qIdx) => ({
                      id: `q-${qIdx}-${Date.now()}`,
                      questionText: q.questionText ?? "",
                      timestamp: q.timestamp ?? "00:00:00",
                      options:
                        (q.options ?? []).map((opt, optIdx) => ({
                          id: opt.id ?? `opt-${optIdx}-${Date.now()}`,
                          optionText: opt.optionText ?? "",
                          isCorrect: !!opt.isCorrect,
                        })) ?? [],
                    }))
                    .filter((q) => (q.options?.length ?? 0) > 0) ?? [],
              };
            })
            .filter((x): x is NonNullable<typeof x> => !!x)
        : [];
      const nonUnitDurationHours = nonUnitFirstChapter
        ? (nonUnitFirstChapter as { durationHours?: number }).durationHours
        : undefined;
      const nonUnitDurationMinutes = nonUnitFirstChapter
        ? (nonUnitFirstChapter as { durationMinutes?: number }).durationMinutes
        : undefined;
      const documents: DocumentItem[] =
        Array.isArray(d.documentFiles) && d.documentFiles.length > 0
          ? d.documentFiles.map((f, i) => ({
              id: `doc-${i}-${d.id}`,
              name:
                (f.originalName && f.originalName.trim()) ||
                `${t("createEducation.documents.document")} ${i + 1}`,
              fileID: f.id,
              isVisibleToTrainee: true,
            }))
          : (d.documentFileIDs ?? []).map((fileID, i) => ({
              id: `doc-${i}-${d.id}`,
              name: `${t("createEducation.documents.document")} ${i + 1}`,
              fileID,
              isVisibleToTrainee: true,
            }));
      const instructors: InstructorItem[] = (d.instructors ?? []).map((i) => ({
        id: i.userID ?? "",
        name:
          ([i.firstName, i.lastName].filter(Boolean).join(" ").trim() ||
            i.userID) ??
          "",
        tcKimlikNo: "",
      }));

      // Eğitim Bazlı Yöneticiler — BE response'ta managers alanı
      const managers = (d.managers ?? []).map((m) => ({
        id: m.userID ?? "",
        name:
          ([m.firstName, m.lastName].filter(Boolean).join(" ").trim() ||
            m.email ||
            m.userID) ??
          "",
        email: m.email ?? undefined,
      }));
      const scopeTypeRaw =
        d.scopeType ?? (d as { scope_type?: number }).scope_type ?? 2;
      const scopeType =
        typeof scopeTypeRaw === "string"
          ? parseInt(scopeTypeRaw, 10)
          : Number(scopeTypeRaw);

      setFormData({
        targetAudience: scopeType as 0 | 1 | 2,
        educationFormat: typeToFormat[d.type] ?? "face-to-face",
        educationCode: d.code ?? "",
        educationName: d.name ?? "",
        educationDescription: (d.description as string | null | undefined) ?? "",
        educationContent: (d.content as string | null | undefined) ?? "",
        coverImage: null,
        coverImageFileId: d.coverImageFileID ?? "",
        coverImagePreview: d.coverImageUrl ?? "",
        documents,
        units,
        nonUnitObjectives,
        nonUnitDurationHours,
        nonUnitDurationMinutes,
        isMandatory: d.isRequired === true,
        isCollectable: intentIsCollectable,
        category: d.categories?.[0]?.id ?? "",
        instructors,
        managers,
        hasBudgetRequirement:
          (d as { isWaitingBudgedApprovement?: boolean })
            .isWaitingBudgedApprovement === false
            ? false
            : (d.budget ?? 0) > 0,
        budget: d.budget != null ? String(d.budget) : "",
        examDefinition:
          typeof d.exam === "object" && d.exam?.id
            ? d.exam.id
            : typeof d.exam === "string"
              ? d.exam
              : "",
        survey:
          typeof d.survey === "string"
            ? d.survey
            : d.survey && typeof (d.survey as { id?: string }).id === "string"
              ? (d.survey as { id: string }).id
              : "",
        certificate:
          d.certificateId ??
          (d as { certificate_id?: string }).certificate_id ??
          "",
        approvalFlow: (d as { approvalFlowId?: string | null }).approvalFlowId ?? "",
        startDate: d.startDate ? new Date(d.startDate) : null,
        durationHours: d.durationHours != null ? String(d.durationHours) : "",
        durationMinutes:
          d.durationMinutes != null ? String(d.durationMinutes) : "",
        requestCollectionDateRange: [reqStart, reqEnd],
        completionDate: d.endDate ? new Date(d.endDate) : null,
        capacity: d.capacity != null ? String(d.capacity) : "",
        location: d.location ?? "",
        latitude: d.latitude != null ? String(d.latitude) : "",
        longitude: d.longitude != null ? String(d.longitude) : "",
        presetLocationId: d.presetLocationId ?? "",
        transportation: d.transportation ?? "",
      });
      setInitialPresetLocationId(d.presetLocationId ?? "");
      // Edit modunda: mevcut presetLocationId dolu ise PRESET, aksi halde MANUAL
      setLocationMode(
        (d.presetLocationId ?? "").trim().length > 0 ? "PRESET" : "MANUAL",
      );
      setEducationApiStatus(d.status);
      setEnrolledCount(
        Math.max(
          (d as { requestedEnrollmentsCount?: number }).requestedEnrollmentsCount ?? 0,
          (d as { totalEnrolledCount?: number }).totalEnrolledCount ?? 0,
        ),
      );
      setDetailApplied(true);
      const detailWithBudget = d as {
        isWaitingBudgedApprovement?: boolean;
        isBudgetApproved?: boolean;
      };
      setEducationIsWaitingBudgetApproval(
        detailWithBudget.isWaitingBudgedApprovement ?? false,
      );
      setEducationIsBudgetApproved(detailWithBudget.isBudgetApproved ?? false);
    },
    [t],
  );

  useEffect(() => {
    if (!educationId) return;
    let cancelled = false;
    setDetailApplied(false);
    setIsLoadingDetail(true);
    educationManagementApi
      .getById(educationId)
      .then(({ data }) => {
        if (cancelled) return;
        // Edit sayfası gate: strict EDUCATION_SUPERVISOR yalnızca eğitime
        // per-education yönetici olarak atanmışsa düzenleyebilir. Backend GET
        // detail endpoint'i officer akışı için "kendi departmanından başvuru
        // yapılan eğitim" görünürlüğüne de izin verdiğinden, edit sayfasını
        // sadece atanmış manager'a açan ek bir gate burada uygulanır.
        const { userType: currentUserType, currentUser } = useStore.getState();
        if (currentUserType === UserRoleType.EDUCATION_SUPERVISOR) {
          const myId = currentUser?.id;
          const isAssignedManager = (data.managers ?? []).some(
            (m) => m.userID && m.userID === myId,
          );
          if (!isAssignedManager) {
            navigate("/dashboard/unauthorized", { replace: true });
            return;
          }
        }
        if (import.meta.env.DEV) {
          console.debug("[CreateEducationPage] detail loaded:", {
            id: data.id,
            description: data.description,
            content: data.content,
          });
        }
        setFormFromDetail(data);
        // Mevcut yöneticileri managerOptions'a ekle — chip etiketleri
        // doğru görünsün diye
        const loadedManagers = (data.managers ?? []).map((m) => ({
          id: m.userID ?? "",
          name:
            [m.firstName, m.lastName].filter(Boolean).join(" ").trim() ||
            m.email ||
            m.userID ||
            "",
          email: m.email ?? undefined,
        }));
        if (loadedManagers.length > 0) {
          fetchManagerUsers("", loadedManagers);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Atanmamış EDUCATION_SUPERVISOR backend EducationAccessGuard'dan
        // 403 EDUCATION_ACCESS_DENIED alır — bu sayfayı görme yetkisi yok.
        const status = (err as { response?: { status?: number } } | null)
          ?.response?.status;
        if (status === 403) {
          navigate("/dashboard/unauthorized", { replace: true });
          return;
        }
        toast.error(t("createEducation.loadError"));
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoadingDetail(false);
      });
    return () => {
      cancelled = true;
    };
  }, [educationId, setFormFromDetail, t, fetchManagerUsers, navigate]);

  // Zorunlu eğitim sağlık durumu — yalnızca edit modda ve form isRequired iken.
  // Her 30sn bir ve reconcile sonrası yenilenir; isHealthy=false iken banner açılır.
  useEffect(() => {
    if (!educationId || !formData.isMandatory) {
      setMandatoryHealth(null);
      return;
    }
    let cancelled = false;
    const fetch = () =>
      educationManagementApi
        .getMandatoryHealth(educationId)
        .then((health) => {
          if (!cancelled) setMandatoryHealth(health);
        })
        .catch(() => {
          /* sağlık endpoint'i başarısızsa banner'ı göstermiyoruz — sessiz */
        });
    fetch();
    const interval = window.setInterval(fetch, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [educationId, formData.isMandatory]);

  const handleOpenMissingUsers = async () => {
    if (!educationId) return;
    setIsMissingUsersOpen(true);
    setIsLoadingMissingUsers(true);
    try {
      const res = await educationManagementApi.getMandatoryMissingUsers(
        educationId,
      );
      setMissingUsers(res.users);
    } catch {
      toast.error(
        t("createEducation.mandatory.missingUsersError"),
      );
    } finally {
      setIsLoadingMissingUsers(false);
    }
  };

  const handleReconcileMandatory = async () => {
    if (!educationId || isReconciling) return;
    setIsReconciling(true);
    try {
      await educationManagementApi.reconcileMandatory(educationId);
      toast.success(
        t("createEducation.mandatory.reconcileQueued"),
      );
      // 3sn sonra health'i tekrar çek — job'un işlenmesine zaman tanıyoruz.
      window.setTimeout(() => {
        void educationManagementApi
          .getMandatoryHealth(educationId)
          .then((health) => setMandatoryHealth(health))
          .catch(() => {});
      }, 3_000);
    } catch (err) {
      toast.error(
        (err as { message?: string })?.message ??
          t("createEducation.mandatory.reconcileError"),
      );
    } finally {
      setIsReconciling(false);
    }
  };

  const handleGoBack = () => {
    navigate("/dashboard/education-management");
  };

  const breadcrumbItems = [
    {
      label: t("createEducation.breadcrumb.educationManagement"),
      onClick: () => navigate("/dashboard/education-management"),
    },
    {
      label: isEditMode
        ? t("createEducation.breadcrumb.editEducation")
        : t("createEducation.breadcrumb.createEducation"),
    },
  ];

  // Dropdown options: 0 CENTRAL, 1 BRANCH, 2 ALL (value string for Dropdown, form state is number)
  const targetAudienceOptions = [
    {
      label: t("createEducation.basicInfo.targetAudienceOptions.central"),
      value: String(SCOPE_TYPE_API.CENTRAL),
    },
    {
      label: t("createEducation.basicInfo.targetAudienceOptions.branch"),
      value: String(SCOPE_TYPE_API.BRANCH),
    },
    {
      label: t("createEducation.basicInfo.targetAudienceOptions.all"),
      value: String(SCOPE_TYPE_API.ALL),
    },
  ];

  const educationFormatOptions = [
    {
      label: t("createEducation.basicInfo.educationFormatOptions.faceToFace"),
      value: "face-to-face",
    },
    {
      label: t("createEducation.basicInfo.educationFormatOptions.online"),
      value: "online",
    },
    {
      label: t("createEducation.basicInfo.educationFormatOptions.nonUnitBased"),
      value: "non-unit-based",
    },
    {
      label: t("createEducation.basicInfo.educationFormatOptions.unitBased"),
      value: "unit-based",
    },
  ];

  const presetLocationOptions = presetLocations.map((loc) => ({
    label: `${loc.name} — ${loc.address}`,
    value: loc.id,
  }));
  const isPresetLocationSelected = formData.presetLocationId.trim().length > 0;

  const handleInputChange = (field: keyof FormData, value: unknown) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleAddUnit = (unit: UnitItem) => {
    if (editingUnit) {
      setFormData((prev) => ({
        ...prev,
        units: prev.units.map((u) => (u.id === editingUnit.id ? unit : u)),
      }));
      setEditingUnit(null);
    } else {
      setFormData((prev) => ({
        ...prev,
        units: [...prev.units, unit],
      }));
    }
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.units;
      return next;
    });
  };

  const handleEditUnit = (unit: UnitItem) => {
    setEditingUnit(unit);
    setIsUnitModalOpen(true);
  };

  const handleRemoveUnit = (unitId: string) => {
    setFormData((prev) => ({
      ...prev,
      units: prev.units.filter((u) => u.id !== unitId),
    }));
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.units;
      return next;
    });
  };

  const handleCloseUnitModal = () => {
    setIsUnitModalOpen(false);
    setEditingUnit(null);
  };

  const handleOpenUnitModal = () => {
    setEditingUnit(null);
    setIsUnitModalOpen(true);
  };

  const handleAddDocument = (document: DocumentItem) => {
    if (editingDocument) {
      // Update existing document
      setFormData((prev) => ({
        ...prev,
        documents: prev.documents.map((doc) =>
          doc.id === editingDocument.id ? document : doc,
        ),
      }));
      setEditingDocument(null);
    } else {
      // Add new document
      setFormData((prev) => ({
        ...prev,
        documents: [...prev.documents, document],
      }));
    }
  };

  const handleEditDocument = (document: DocumentItem) => {
    setEditingDocument(document);
    setIsDocumentModalOpen(true);
  };

  const handleRemoveDocument = (documentId: string) => {
    setFormData((prev) => ({
      ...prev,
      documents: prev.documents.filter((doc) => doc.id !== documentId),
    }));
  };

  const handleCloseDocumentModal = () => {
    setIsDocumentModalOpen(false);
    setEditingDocument(null);
  };

  /** Kapak görseli backend'e yüklendikten sonra (fileId + önizleme URL) */
  const handleImageUploadSuccess = (fileId: string, previewUrl?: string) => {
    setFormData((prev) => ({
      ...prev,
      coverImageFileId: fileId,
      coverImagePreview: previewUrl ?? prev.coverImagePreview,
    }));
    setIsCoverUploading(false);
  };

  /** Kapak görselini kaldır */
  const handleImageRemove = () => {
    setFormData((prev) => ({
      ...prev,
      coverImage: null,
      coverImageFileId: "",
      coverImagePreview: "",
    }));
  };

  /**
   * Şablon kodu lookup: kullanıcı code input'tan ayrıldığında ya da generate
   * butonu sonrası tetiklenir. Yalnızca create modunda; edit'te skip.
   * Aynı kod için tekrar tekrar sormamak için lastLookedUpCodeRef tutulur.
   */
  const handleTemplateLookup = async (rawCode: string) => {
    if (isEditMode) return;
    const trimmed = rawCode.trim();
    if (!trimmed) return;
    if (lastLookedUpCodeRef.current === trimmed) return;
    lastLookedUpCodeRef.current = trimmed;
    try {
      setIsLookingUpTemplate(true);
      const prefill = await educationManagementApi.lookupTemplate(trimmed);
      if (!prefill) return;
      setTemplatePrefillData(prefill);
      setTemplatePrefillModalOpen(true);
    } catch {
      // sessizce yut — şablon lookup zorunlu değil
    } finally {
      setIsLookingUpTemplate(false);
    }
  };

  /** Modal'da "evet, doldur" tıklanırsa form'u prefill verisiyle doldurur. */
  const handleConfirmTemplatePrefill = () => {
    if (!templatePrefillData) {
      setTemplatePrefillModalOpen(false);
      return;
    }
    const data = templatePrefillData;
    const totalMinutes = Math.max(0, data.durationMinutes || 0);
    const hoursPart = Math.floor(totalMinutes / 60);
    const minutesPart = totalMinutes % 60;
    setFormData((prev) => ({
      ...prev,
      educationName: data.name,
      educationDescription: data.description ?? "",
      educationContent: data.content ?? "",
      durationHours: hoursPart > 0 ? String(hoursPart) : "",
      durationMinutes: minutesPart > 0 ? String(minutesPart) : "",
      educationFormat: typeToFormat[data.type] ?? prev.educationFormat,
      targetAudience: data.scopeType ?? prev.targetAudience,
      category: data.categoryIDs[0] ?? prev.category,
    }));
    setTemplatePrefillModalOpen(false);
    setTemplatePrefillData(null);
  };

  /** Form değerlerini backend POST body'ye çevirir */
  const buildCreatePayload = (isDraft: boolean): AdminCreateEducationDto => {
    const formatToType: Record<string, number> = {
      "face-to-face": EDUCATION_TYPE_API.ON_SITE,
      online: EDUCATION_TYPE_API.ONLINE,
      "unit-based": EDUCATION_TYPE_API.UNIT,
      "non-unit-based": EDUCATION_TYPE_API.NONUNIT,
    };
    const [reqStart, reqEnd] = formData.requestCollectionDateRange;
    const hasPresetLocation = formData.presetLocationId.trim().length > 0;

    // Status belirleme:
    // - Taslak kaydet -> DRAFT (5)
    // - Yayınla:
    //   - Bütçe gerekiyor ve henüz onaylanmamışsa -> BUDGET_PENDING (1)
    //   - Zorunlu eğitim -> ACTIVE (2): talep toplama olmaz, trainee'lere
    //     doğrudan atanır, oluşturma anında yayında olmalı.
    //   - "Talep Toplanabilir" açık ise -> REQUEST_COLLECTION (0)
    //   - "Talep Toplanabilir" kapalı (manuel atama modu) -> DRAFT (5)
    //     Trainee'ye görünürlük ayrı bir explicit publish aksiyonuyla
    //     (PATCH {status:2}) sağlanır; oluşturma anında trainee görmemeli.
    const status: number | undefined = (() => {
      if (isDraft) return EDUCATION_STATUS_API.DRAFT;
      if (formData.hasBudgetRequirement && !educationIsBudgetApproved)
        return EDUCATION_STATUS_API.BUDGET_PENDING;
      if (formData.isMandatory) return EDUCATION_STATUS_API.ACTIVE;
      if (formData.isCollectable)
        return EDUCATION_STATUS_API.REQUEST_COLLECTION;
      return EDUCATION_STATUS_API.DRAFT;
    })();
    // Bayrak kullanıcının seçimini taşır, status'ten türetilmez: taslak kaydedilen
    // (status=DRAFT) bir eğitimde de "Talep Toplanabilir" açık kalmalı.
    const isAcceptingRequestCollection =
      !formData.isMandatory && formData.isCollectable;
    const trimmedCode = formData.educationCode.trim();
    const payload: AdminCreateEducationDto = {
      code: trimmedCode || undefined,
      // Backend `<YYYY>-<code>` olarak kaydedip her sene tekrarı destekler.
      // Yalnızca code dolu ise gönderilir; boş ise default false geçerli.
      autoYearPrefix: trimmedCode ? true : undefined,
      name: formData.educationName.trim(),
      description: formData.educationDescription.trim() || undefined,
      content: formData.educationContent.trim() || undefined,
      type:
        formatToType[formData.educationFormat] ?? EDUCATION_TYPE_API.ON_SITE,
      scopeType: formData.targetAudience,
      status,
      // Zorunlu eğitimde RC bayrakları yok sayılır; backend de override
      // ediyor (educations.service.normalizeMandatoryFlags) — burada
      // payload'ı temiz gönderiyoruz ki mobil/admin paritesi bozulmasın.
      isRequired: formData.isMandatory,
      isAcceptingRequestCollection: formData.isMandatory
        ? false
        : isAcceptingRequestCollection,
      categoryIds: formData.category ? [formData.category] : undefined,
      coverImageFileID: formData.coverImageFileId || undefined,
      documentFileIDs: (() => {
        const ids = formData.documents
          .map((d) => d.fileID)
          .filter((id): id is string => !!id);
        return ids.length > 0 ? ids : undefined;
      })(),
      examId: formData.examDefinition || undefined,
      surveyId: formData.survey || undefined,
      certificateId: formData.certificate.trim() || undefined,
      // Boş seçim: create'te alan gönderilmez, update'te null ile seçim kaldırılır.
      approvalFlowId: formData.approvalFlow.trim() || undefined,
      budget:
        formData.hasBudgetRequirement && formData.budget
          ? Number(formData.budget)
          : undefined,
      capacity: (() => {
        const n = Number(formData.capacity);
        if (!formData.capacity.trim() || Number.isNaN(n)) return undefined;
        return Math.min(100000, Math.max(0, Math.floor(n)));
      })(),
      durationHours: (() => {
        const h = parseOptionalNumber(formData.durationHours);
        const m = parseOptionalNumber(formData.durationMinutes);
        if (h === undefined && m === undefined) return undefined;
        const total = (h ?? 0) * 60 + (m ?? 0);
        if (total < 1) return undefined;
        return h ?? 0;
      })(),
      durationMinutes: (() => {
        const h = parseOptionalNumber(formData.durationHours);
        const m = parseOptionalNumber(formData.durationMinutes);
        if (h === undefined && m === undefined) return undefined;
        const total = (h ?? 0) * 60 + (m ?? 0);
        if (total < 1) return undefined;
        return m ?? 0;
      })(),
      startDate: toStartOfDayISO(formData.startDate),
      requestCollectionStartDate:
        formData.isMandatory
          ? undefined
          : formData.isCollectable
            ? toStartOfDayISO(reqStart)
            : undefined,
      requestCollectionEndDate:
        formData.isMandatory
          ? undefined
          : formData.isCollectable
            ? toEndOfDayISO(reqEnd)
            : undefined,
      endDate: toEndOfDayISO(formData.completionDate),
      chapters: mergeEducationChapters(formData),
      instructorIds:
        formData.instructors.length > 0
          ? formData.instructors.map((i) => i.id)
          : undefined,
      // Eğitim Bazlı Yönetici ID listesi (Madde 5). BE DTO alanı: educationManagerIds.
      // Strict EDUCATION_SUPERVISOR yönetici listesini değiştiremez (yetki
      // yükseltme zinciri kapalı). Backend bu alanı göndermesi durumunda
      // 403 atar — değiştirmediği halde hata almasın diye payload'a hiç koyma.
      educationManagerIds: isStrictEducationSupervisorUser
        ? undefined
        : formData.managers.length > 0
          ? formData.managers.map((m) => m.id)
          : undefined,
      presetLocationId: hasPresetLocation
        ? formData.presetLocationId
        : undefined,
      location: hasPresetLocation
        ? undefined
        : formData.location.trim() || undefined,
      latitude: hasPresetLocation
        ? undefined
        : (() => {
            const n = Number(formData.latitude);
            return Number.isNaN(n) ? undefined : n;
          })(),
      longitude: hasPresetLocation
        ? undefined
        : (() => {
            const n = Number(formData.longitude);
            return Number.isNaN(n) ? undefined : n;
          })(),
      transportation: formData.transportation.trim() || undefined,
      isWaitingBudgedApprovement:
        formData.hasBudgetRequirement && !educationIsBudgetApproved,
    };
    return payload;
  };

  /** isDraft: true = Taslak (status 5), false = Yayınla (status 0 veya 2) */
  const buildPatchPayload = (isDraft: boolean): AdminPatchEducationDto => {
    const formatToType: Record<string, number> = {
      "face-to-face": EDUCATION_TYPE_API.ON_SITE,
      online: EDUCATION_TYPE_API.ONLINE,
      "unit-based": EDUCATION_TYPE_API.UNIT,
      "non-unit-based": EDUCATION_TYPE_API.NONUNIT,
    };
    const [reqStart, reqEnd] = formData.requestCollectionDateRange;
    const hasPresetLocation = formData.presetLocationId.trim().length > 0;
    const shouldClearPreset =
      !hasPresetLocation && initialPresetLocationId.trim().length > 0;
    const mergedChapters = mergeEducationChapters(formData);
    const chapters =
      mergedChapters !== undefined
        ? mergedChapters
        : formData.educationFormat === "online" ||
            formData.educationFormat === "non-unit-based"
          ? []
          : undefined;
    const isFormatLocked =
      educationApiStatus !== null &&
      educationApiStatus !== EDUCATION_STATUS_API.DRAFT;
    const status = (() => {
      // Yayınlanmış (DRAFT dışı) eğitimde normal Kaydet status'u değiştirmez;
      // status geçişleri explicit aksiyonlarla (Başlat/Tamamla) yapılır.
      if (
        !isDraft &&
        educationApiStatus !== null &&
        educationApiStatus !== EDUCATION_STATUS_API.DRAFT
      ) {
        // Yayındaki eğitimin statüsü korunur; tek istisna talep toplamanın
        // açılması. ACTIVE + talep toplama kombinasyonu katalogda gösterilmiyor
        // (backend educations.repository), bu yüzden toggle açıldığında statü
        // toplu "talebe aç" aksiyonundaki gibi REQUEST_COLLECTION'a döner.
        if (
          educationApiStatus === EDUCATION_STATUS_API.ACTIVE &&
          !formData.isMandatory &&
          formData.isCollectable
        )
          return EDUCATION_STATUS_API.REQUEST_COLLECTION;
        return educationApiStatus;
      }
      if (isDraft) return EDUCATION_STATUS_API.DRAFT;
      if (formData.hasBudgetRequirement && !educationIsBudgetApproved)
        return EDUCATION_STATUS_API.BUDGET_PENDING;
      // Zorunlu eğitim -> ACTIVE (2): talep toplama yok, trainee'lere doğrudan
      // atanır; yayın aksiyonuyla taslakta bırakılmamalı.
      if (formData.isMandatory) return EDUCATION_STATUS_API.ACTIVE;
      if (formData.isCollectable)
        return EDUCATION_STATUS_API.REQUEST_COLLECTION;
      // "Talep Toplanabilir" kapalı (manuel atama modu) -> DRAFT (5).
      // Trainee'ye görünürlük ayrı bir explicit publish aksiyonuyla sağlanır.
      return EDUCATION_STATUS_API.DRAFT;
    })();
    // Bayrak yalnızca kullanıcının seçimini takip eder.
    //
    // Eskiden status de koşuldu (`status === REQUEST_COLLECTION && ...`) ve ayar
    // kaydedilince kalıcı olmuyordu: yayındaki eğitimde status korunduğu için
    // (ör. ACTIVE) ya da taslak kaydedildiğinde (DRAFT) bayrak false gidiyor,
    // backend `isAcceptingRequestCollection=false` görünce tarihleri de
    // null'lıyordu (normalizeRequestCollectionUpdateDto). Ekran yeniden
    // yüklendiğinde ne bayrak ne tarih kaldığı için toggle kapalı geliyordu.
    const isAcceptingRequestCollection =
      !formData.isMandatory && formData.isCollectable;
    return {
      code: formData.educationCode.trim() || undefined,
      name: formData.educationName.trim(),
      description: formData.educationDescription.trim() || undefined,
      content: formData.educationContent.trim() || undefined,
      // Publish sonrası format/scope değişikliği serverda da zorlanmalı;
      // payload'dan çıkararak istenmeden silmeyi engelliyoruz.
      type: isFormatLocked
        ? undefined
        : (formatToType[formData.educationFormat] ?? EDUCATION_TYPE_API.ON_SITE),
      scopeType: isFormatLocked ? undefined : formData.targetAudience,
      status,
      categoryIds: formData.category ? [formData.category] : undefined,
      coverImageFileID: formData.coverImageFileId.trim()
        ? formData.coverImageFileId
        : null,
      documentFileIDs:
        formData.documents
          .map((d) => d.fileID)
          .filter((id): id is string => !!id) || undefined,
      examId: formData.examDefinition || undefined,
      surveyId: formData.survey || undefined,
      certificateId: formData.certificate.trim() || undefined,
      approvalFlowId: formData.approvalFlow.trim() || null,
      budget:
        formData.hasBudgetRequirement && formData.budget
          ? Number(formData.budget)
          : undefined,
      capacity: (() => {
        const n = Number(formData.capacity);
        if (!formData.capacity.trim() || Number.isNaN(n)) return undefined;
        return Math.min(100000, Math.max(0, Math.floor(n)));
      })(),
      durationHours: (() => {
        const h = parseOptionalNumber(formData.durationHours);
        const m = parseOptionalNumber(formData.durationMinutes);
        if (h === undefined && m === undefined) return undefined;
        const total = (h ?? 0) * 60 + (m ?? 0);
        if (total < 1) return undefined;
        return h ?? 0;
      })(),
      durationMinutes: (() => {
        const h = parseOptionalNumber(formData.durationHours);
        const m = parseOptionalNumber(formData.durationMinutes);
        if (h === undefined && m === undefined) return undefined;
        const total = (h ?? 0) * 60 + (m ?? 0);
        if (total < 1) return undefined;
        return m ?? 0;
      })(),
      startDate: toStartOfDayISO(formData.startDate) ?? null,
      requestCollectionStartDate:
        formData.isMandatory
          ? null
          : formData.isCollectable
            ? toStartOfDayISO(reqStart)
            : undefined,
      requestCollectionEndDate:
        formData.isMandatory
          ? null
          : formData.isCollectable
            ? toEndOfDayISO(reqEnd)
            : undefined,
      endDate: toEndOfDayISO(formData.completionDate) ?? null,
      // Zorunlu eğitimde RC bayrağı her zaman false — backend de override eder.
      isRequired: formData.isMandatory,
      isAcceptingRequestCollection: formData.isMandatory
        ? false
        : isAcceptingRequestCollection,
      instructorIds:
        formData.instructors.length > 0
          ? formData.instructors.map((i) => i.id)
          : undefined,
      // Eğitim Bazlı Yönetici ID listesi (Madde 5). BE DTO alanı: educationManagerIds.
      // Strict SUPERVISOR yönetici listesini değiştiremez — payload'a hiç koyma
      // ki "değiştirmediği" halde 403 EDUCATION_MANAGER_ASSIGNMENT_FORBIDDEN almasın.
      educationManagerIds: isStrictEducationSupervisorUser
        ? undefined
        : formData.managers.length > 0
          ? formData.managers.map((m) => m.id)
          : undefined,
      chapters,
      presetLocationId: hasPresetLocation
        ? formData.presetLocationId
        : shouldClearPreset
          ? null
          : undefined,
      location: hasPresetLocation
        ? undefined
        : formData.location.trim() || undefined,
      latitude: hasPresetLocation
        ? undefined
        : (() => {
            const n = Number(formData.latitude);
            return Number.isNaN(n) ? undefined : n;
          })(),
      longitude: hasPresetLocation
        ? undefined
        : (() => {
            const n = Number(formData.longitude);
            return Number.isNaN(n) ? undefined : n;
          })(),
      transportation: formData.transportation.trim() || undefined,
      isWaitingBudgedApprovement:
        formData.hasBudgetRequirement && !educationIsBudgetApproved,
    };
  };

  const getResponseMessage = (
    data: { message?: string; messages?: string[] } | undefined,
  ) => {
    if (!data) return undefined;
    if (typeof data.message === "string" && data.message.trim())
      return data.message.trim();
    if (Array.isArray(data.messages) && data.messages.length > 0) {
      return data.messages
        .map((m) => (typeof m === "string" ? m : String(m)))
        .join(" ");
    }
    return undefined;
  };

  /** Eğitim yayına alındıktan sonra tekrar taslağa çekilemez */
  const canSaveAsDraft =
    !isEditMode ||
    educationApiStatus === null ||
    educationApiStatus === EDUCATION_STATUS_API.DRAFT;
  const isAlreadyPublished =
    isEditMode &&
    educationApiStatus !== null &&
    educationApiStatus !== EDUCATION_STATUS_API.DRAFT;

  const handleSaveDraft = async () => {
    if (!canSaveAsDraft) {
      toast.error(
        t("createEducation.validation.draftNotAllowed"),
      );
      return;
    }
    const validation = validateCreateEducationForm(formData, {
      enrolledCount,
      isPublishing: false,
      isAlreadyPublished,
    }, t);
    if (!validation.success) {
      const translatedFieldErrors: Record<string, string> = {};
      Object.entries(validation.fieldErrors).forEach(([key, val]) => {
        translatedFieldErrors[key] = t(val);
      });
      setFieldErrors(translatedFieldErrors);
      const firstTrMsg = validation.errors.find(
        (m) => typeof m === "string" && !/^required$/i.test(m.trim()),
      );
      toast.error(
        (firstTrMsg && t(firstTrMsg)) ??
          t("createEducation.validation.invalid"),
      );
      return;
    }
    setFieldErrors({});
    setIsSubmitting(true);
    try {
      if (isEditMode && educationId) {
        const res = await educationManagementApi.patch(
          educationId,
          buildPatchPayload(true),
        );
        const data = (
          res as { data?: { message?: string; messages?: string[] } }
        )?.data;
        const msg = getResponseMessage(data);
        toast.success(
          msg ?? t("createEducation.updateSuccess"),
        );
      } else {
        const res = await educationManagementApi.create(
          buildCreatePayload(true),
        );
        const data = (
          res as { data?: { message?: string; messages?: string[] } }
        )?.data;
        const msg = getResponseMessage(data);
        toast.success(
          msg ??
            t("createEducation.draftSuccess"),
        );
      }
      setFieldErrors({});
      navigate("/dashboard/education-management");
    } catch (err: unknown) {
      const data = (
        err as {
          response?: {
            status?: number;
            data?: { message?: string; messages?: string[] };
          };
        }
      )?.response?.data;
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      const msg = getResponseMessage(data);
      if (status === 409) {
        const isNameConflict = /eğitim\s*ad|education\s*name/i.test(msg ?? "");
        setFieldErrors((prev) =>
          isNameConflict
            ? {
                ...prev,
                educationName:
                  msg ?? t("createEducation.errors.educationNameDuplicate"),
              }
            : {
                ...prev,
                educationCode:
                  msg ?? t("createEducation.errors.educationCodeDuplicate"),
              },
        );
      }
      toast.error(
        msg ??
          t("createEducation.saveError"),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePublish = async () => {
    const validation = validateCreateEducationForm(formData, {
      enrolledCount,
      isPublishing: true,
      isAlreadyPublished,
    }, t);
    if (!validation.success) {
      const translatedFieldErrors: Record<string, string> = {};
      Object.entries(validation.fieldErrors).forEach(([key, val]) => {
        translatedFieldErrors[key] = t(val);
      });
      setFieldErrors(translatedFieldErrors);
      const firstTrMsg = validation.errors.find(
        (m) => typeof m === "string" && !/^required$/i.test(m.trim()),
      );
      toast.error(
        (firstTrMsg && t(firstTrMsg)) ??
          t("createEducation.validation.invalid"),
      );
      return;
    }
    setFieldErrors({});
    setIsSubmitting(true);
    try {
      if (isEditMode && educationId) {
        const res = await educationManagementApi.patch(
          educationId,
          buildPatchPayload(false),
        );
        const data = (
          res as { data?: { message?: string; messages?: string[] } }
        )?.data;
        const msg = getResponseMessage(data);
        toast.success(
          msg ?? t("createEducation.updateSuccess"),
        );
      } else {
        const res = await educationManagementApi.create(
          buildCreatePayload(false),
        );
        const data = (
          res as { data?: { message?: string; messages?: string[] } }
        )?.data;
        const msg = getResponseMessage(data);
        toast.success(
          msg ?? t("createEducation.publishSuccess"),
        );
      }
      setFieldErrors({});
      navigate("/dashboard/education-management");
    } catch (err: unknown) {
      const data = (
        err as {
          response?: {
            status?: number;
            data?: { message?: string; messages?: string[] };
          };
        }
      )?.response?.data;
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      const msg = getResponseMessage(data);
      if (status === 409) {
        const isNameConflict = /eğitim\s*ad|education\s*name/i.test(msg ?? "");
        setFieldErrors((prev) =>
          isNameConflict
            ? {
                ...prev,
                educationName:
                  msg ?? t("createEducation.errors.educationNameDuplicate"),
              }
            : {
                ...prev,
                educationCode:
                  msg ?? t("createEducation.errors.educationCodeDuplicate"),
              },
        );
      }
      toast.error(
        msg ??
          t("createEducation.saveError"),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApproveBudget = async () => {
    if (!educationId) return;
    setIsApprovingBudget(true);
    try {
      await educationManagementApi.patch(educationId, {
        isBudgetApproved: true,
      });
      setEducationIsBudgetApproved(true);
      setEducationIsWaitingBudgetApproval(false);
      toast.success(t("createEducation.budgetApproved"));
      // Onay sonrası backend'in türettiği alanları (statü vs.) form state'ine
      // tazele — handleStartNewPeriod / handleStartEducation aynı örüntüyü uygular.
      setDetailApplied(false);
      setIsLoadingDetail(true);
      educationManagementApi
        .getById(educationId)
        .then(({ data }) => setFormFromDetail(data))
        .catch(() =>
          toast.error(t("createEducation.loadError")),
        )
        .finally(() => setIsLoadingDetail(false));
    } catch (err: unknown) {
      const data = (
        err as {
          response?: { data?: { message?: string; messages?: string[] } };
        }
      )?.response?.data;
      const msg = getResponseMessage(data);
      toast.error(
        msg ??
          t("createEducation.budgetApproveError"),
      );
    } finally {
      setIsApprovingBudget(false);
    }
  };

  const handleStartNewPeriod = async () => {
    if (!educationId) return;
    setIsStartingNewPeriod(true);
    try {
      await educationManagementApi.startNewPeriod(educationId);
      toast.success(
        t("createEducation.newPeriodSuccess"),
      );
      setIsNewPeriodModalOpen(false);
      setDetailApplied(false);
      setIsLoadingDetail(true);
      educationManagementApi
        .getById(educationId)
        .then(({ data }) => setFormFromDetail(data))
        .catch(() =>
          toast.error(t("createEducation.loadError")),
        )
        .finally(() => setIsLoadingDetail(false));
    } catch (err: unknown) {
      const data = (
        err as {
          response?: { data?: { message?: string; messages?: string[] } };
        }
      )?.response?.data;
      const msg = getResponseMessage(data);
      toast.error(
        msg ??
          t("createEducation.newPeriodError"),
      );
    } finally {
      setIsStartingNewPeriod(false);
    }
  };

  const handleEducationCompleted = () => {
    if (!educationId) return;
    setDetailApplied(false);
    setIsLoadingDetail(true);
    educationManagementApi
      .getById(educationId)
      .then(({ data }) => setFormFromDetail(data))
      .catch(() =>
        toast.error(t("createEducation.loadError")),
      )
      .finally(() => setIsLoadingDetail(false));
  };

  const isEducationClosed =
    educationApiStatus === EDUCATION_STATUS_API.CLOSED;
  /** Eğitim talep toplama veya aktif duruma geçebilir mi? (Başvuru sürecini kapatıp başlatır) */
  const canStartEducation =
    isEditMode &&
    (educationApiStatus === EDUCATION_STATUS_API.REQUEST_COLLECTION ||
      (educationApiStatus === EDUCATION_STATUS_API.BUDGET_PENDING &&
        educationIsBudgetApproved) ||
      // Talep toplama kapalı (manuel atama) eğitim "Yayınla" ile DRAFT'ta
      // kalıyor; kursiyere açan tek yol bu buton. Bütçe onayı bekleyen taslak
      // için gösterilmez, o önce yayınlanıp bütçe akışına girmeli.
      (educationApiStatus === EDUCATION_STATUS_API.DRAFT &&
        !formData.isCollectable &&
        !(formData.hasBudgetRequirement && !educationIsBudgetApproved)));
  const canCompleteEducation =
    isEditMode &&
    isEducationManager &&
    educationApiStatus === EDUCATION_STATUS_API.ACTIVE;

  const handleStartEducation = async () => {
    if (!educationId) return;
    setIsStartingEducation(true);
    try {
      await educationManagementApi.patch(educationId, {
        status: EDUCATION_STATUS_API.ACTIVE,
        isAcceptingRequestCollection: false,
      });
      toast.success(
        t("createEducation.startSuccess"),
      );
      setIsStartModalOpen(false);
      setDetailApplied(false);
      setIsLoadingDetail(true);
      educationManagementApi
        .getById(educationId)
        .then(({ data }) => setFormFromDetail(data))
        .catch(() =>
          toast.error(t("createEducation.loadError")),
        )
        .finally(() => setIsLoadingDetail(false));
    } catch (err: unknown) {
      const data = (
        err as {
          response?: { data?: { message?: string; messages?: string[] } };
        }
      )?.response?.data;
      const msg = getResponseMessage(data);
      toast.error(
        msg ??
          t("createEducation.startError"),
      );
    } finally {
      setIsStartingEducation(false);
    }
  };

  const isBudgetRequirementSwitchDisabled =
    isEditMode &&
    educationIsBudgetApproved === true &&
    educationIsWaitingBudgetApproval === true;
  const durationError =
    fieldErrors.durationMinutes ??
    fieldErrors.durationHours ??
    fieldErrors.durationTotal;

  return (
    <DashboardLayout>
      <Header
        title={
          isEditMode
            ? t("createEducation.titleEdit")
            : t("createEducation.title")
        }
      />

      <div className={styles.container}>
        {/* Header with Breadcrumb and Actions */}
        <div className={styles.pageHeader}>
          <div className={styles.breadcrumbContainer}>
            <button className={styles.backButton} onClick={handleGoBack}>
              <LeftChevron width={20} height={20} />
            </button>
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        {/* Zorunlu eğitim atama sağlık banner'ı — yalnızca isRequired + edit
            modda + backend isHealthy=false döndürdüyse görünür. Normal akışta
            hiç render edilmez, admin UI'ı kalabalıklaşmaz. */}
        {isEditMode &&
          formData.isMandatory &&
          mandatoryHealth &&
          !mandatoryHealth.isHealthy && (
            <div className={styles.mandatoryHealthBanner}>
              <div className={styles.mandatoryHealthBannerText}>
                <strong>
                  {t("createEducation.mandatory.healthBannerTitle")}
                </strong>
                <span>
                  {t(
                    "createEducation.mandatory.healthBannerBody",
                    {
                      expected: mandatoryHealth.expectedAssignmentCount,
                      actual: mandatoryHealth.actualAssignmentCount,
                      missing: mandatoryHealth.missingCount,
                    },
                  )}
                </span>
              </div>
              <div className={styles.mandatoryHealthBannerActions}>
                <Button
                  variant="outlined"
                  color="default"
                  onClick={handleOpenMissingUsers}
                >
                  {t("createEducation.mandatory.listMissingButton")}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleReconcileMandatory}
                  disabled={isReconciling}
                >
                  {isReconciling
                    ? t("common.loading")
                    : t("createEducation.mandatory.reconcileButton")}
                </Button>
              </div>
            </div>
          )}

        {/* Main Content */}
        <div className={styles.mainContent}>
          {isLoadingDetail ? (
            <div className={styles.loadingState}>
              {t("common.loading")}
            </div>
          ) : (
            <>
              {/* Left Section - Main Form */}
              <div className={styles.leftColumn}>
                {/* Basic Info Card */}
                <div className={styles.formCard}>
                  <div className={styles.cardHeader}>
                    <h2 className={styles.cardTitle}>
                      {t("createEducation.basicInfo.title")}
                    </h2>
                    {/* <span className={styles.cardId}>#0001</span> */}
                  </div>

                  <div className={styles.formGrid}>
                    {/* Target Audience & Education Format */}
                    <div className={styles.formRow}>
                      <div className={styles.formField}>
                        <Dropdown
                          key={
                            isEditMode && detailApplied
                              ? `target-audience-${formData.targetAudience}`
                              : "target-audience-init"
                          }
                          label={
                            t("createEducation.basicInfo.targetAudience") + "*"
                          }
                          placeholder={t("createEducation.basicInfo.targetAudiencePlaceholder")}
                          options={targetAudienceOptions}
                          value={String(formData.targetAudience)}
                          selectedLabel={
                            targetAudienceOptions.find(
                              (o) =>
                                o.value === String(formData.targetAudience),
                            )?.label ||
                            t("createEducation.basicInfo.targetAudiencePlaceholder")
                          }
                          onSelect={(value) =>
                            handleInputChange("targetAudience", Number(value))
                          }
                          disabled={isAlreadyPublished}
                          error={!!fieldErrors.targetAudience}
                          errorMessage={fieldErrors.targetAudience}
                        />
                      </div>
                      <div className={styles.formField}>
                        <Dropdown
                          label={
                            t("createEducation.basicInfo.educationFormat") + "*"
                          }
                          placeholder={t("createEducation.basicInfo.educationFormatPlaceholder")}
                          options={educationFormatOptions}
                          value={formData.educationFormat}
                          selectedLabel={
                            educationFormatOptions.find(
                              (o) => o.value === formData.educationFormat,
                            )?.label ||
                            t("createEducation.basicInfo.educationFormatPlaceholder")
                          }
                          onSelect={(value) =>
                            handleInputChange("educationFormat", value)
                          }
                          disabled={isAlreadyPublished}
                          error={!!fieldErrors.educationFormat}
                          errorMessage={fieldErrors.educationFormat}
                        />
                      </div>
                    </div>

                    {/* Education Code */}
                    <div className={styles.formField}>
                      <div className={styles.educationCodeField}>
                        <div className={styles.educationCodeInput}>
                          <Input
                            label={t("createEducation.basicInfo.educationCode")}
                            placeholder={t("createEducation.basicInfo.educationCodePlaceholder")}
                            value={formData.educationCode}
                            onChange={(e) =>
                              handleInputChange(
                                "educationCode",
                                e.target.value,
                              )
                            }
                            onBlur={(e) => handleTemplateLookup(e.target.value)}
                            error={!!fieldErrors.educationCode}
                            errorMessage={fieldErrors.educationCode}
                          />
                        </div>
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={() =>
                            handleInputChange(
                              "educationCode",
                              generateEducationCode(),
                            )
                          }
                          className={styles.educationCodeGenerateButton}
                        >
                          {t("createEducation.basicInfo.generateCode")}
                        </Button>
                      </div>
                    </div>

                    {/* Education Name */}
                    <div className={styles.formField}>
                      <Input
                        label={t("createEducation.basicInfo.educationName")}
                        placeholder={t("createEducation.basicInfo.educationNamePlaceholder")}
                        value={formData.educationName}
                        maxLength={200}
                        onChange={(e) =>
                          handleInputChange("educationName", e.target.value)
                        }
                        required
                        error={!!fieldErrors.educationName}
                        errorMessage={fieldErrors.educationName}
                      />
                    </div>

                    {/* Education Description */}
                    <div className={styles.formField}>
                      <Textarea
                        label={
                          t("createEducation.basicInfo.educationDescription") + "*"
                        }
                        placeholder={t("createEducation.basicInfo.educationDescriptionPlaceholder")}
                        value={formData.educationDescription}
                        maxLength={2000}
                        onChange={(e) =>
                          handleInputChange(
                            "educationDescription",
                            e.target.value,
                          )
                        }
                        rows={4}
                        error={!!fieldErrors.educationDescription}
                      />
                    </div>

                    {/* Education Content */}
                    <div className={styles.formField}>
                      <Textarea
                        label={t("createEducation.basicInfo.educationContent")}
                        placeholder={t("createEducation.basicInfo.educationContentPlaceholder")}
                        value={formData.educationContent}
                        onChange={(e) =>
                          handleInputChange("educationContent", e.target.value)
                        }
                        rows={4}
                      />
                    </div>

                    {/* Cover Image */}
                    <div className={styles.formField}>
                      <FileInput
                        label={t("createEducation.basicInfo.coverImage")}
                        placeholder={t("createEducation.basicInfo.coverImagePlaceholder")}
                        onFileSelect={(files) => {
                          const file = files[0] || null;
                          handleInputChange("coverImage", file);
                          if (file) {
                            setFormData((prev) => ({
                              ...prev,
                              coverImageFileId: "",
                              coverImagePreview: "",
                            }));
                          }
                        }}
                        maxFiles={1}
                        fileType="image"
                        accept={ALLOWED_FILE_TYPE_PRESETS.IMAGE.map(
                          (f) => `.${f.toLowerCase()}`,
                        ).join(",")}
                        supportedFormats={ALLOWED_FILE_TYPE_PRESETS.IMAGE}
                        maxSize={15}
                        uploadToBackend
                        onUploadStart={() => setIsCoverUploading(true)}
                        onUploadSuccess={handleImageUploadSuccess}
                        onFileRemove={() => handleImageRemove()}
                        existingFileUrl={
                          formData.coverImagePreview || undefined
                        }
                        enableDemoProgress={false}
                      />
                    </div>

                    {/* Documents */}
                    <div className={styles.formField}>
                      <label className={styles.fieldLabel}>
                        {t("createEducation.basicInfo.documents")}
                      </label>

                      {/* Document List */}
                      {formData.documents.length > 0 && (
                        <div className={styles.documentList}>
                          {formData.documents.map((doc) => (
                            <div key={doc.id} className={styles.documentItem}>
                              <div className={styles.documentInfo}>
                                <div className={styles.documentIcon}>
                                  <span className={styles.fileCount}>
                                    {formData.documents.length}
                                  </span>
                                </div>
                                <div className={styles.documentDetails}>
                                  <span className={styles.documentName}>
                                    {doc.name}
                                  </span>
                                  <span className={styles.documentStatus}>
                                    {t("createEducation.documents.uploaded")}
                                  </span>
                                </div>
                              </div>
                              <div className={styles.documentActions}>
                                <Button
                                  variant="outlined"
                                  color="default"
                                  size="small"
                                  startIcon={<Edit width={16} height={16} />}
                                  onClick={() => handleEditDocument(doc)}
                                >
                                  {t("createEducation.documents.edit")}
                                </Button>
                                <button
                                  className={styles.removeDocumentButton}
                                  onClick={() => handleRemoveDocument(doc.id)}
                                >
                                  <Close width={16} height={16} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add Document Button */}
                      <div className={styles.addDocumentArea}>
                        <p className={styles.addDocumentText}>
                          {t("createEducation.basicInfo.documentsPlaceholder")}
                        </p>
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={() => setIsDocumentModalOpen(true)}
                        >
                          {t("createEducation.documents.addDocument")}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Üniteler – sadece "Üniteli" formatı seçiliyken gösterilir */}
                {formData.educationFormat === "unit-based" && (
                  <>
                    <div className={styles.unitsCard}>
                      <div className={styles.unitsTitle}>
                        {t("createEducation.units.title")}
                      </div>
                      <div
                        className={styles.addUnitButton}
                        onClick={handleOpenUnitModal}
                      >
                        <Plus width={20} height={20} color="#C51D26" />
                        <span>
                          {t("createEducation.units.addUnit")}
                        </span>
                      </div>
                    </div>

                    {formData.units.length > 0 && (
                      <div className={styles.unitsList}>
                        {formData.units.map((unit, index) => (
                          <div key={unit.id} className={styles.unitItem}>
                            <div className={styles.unitInfo}>
                              <span className={styles.unitNumber}>
                                {index + 1}.
                              </span>
                              <span className={styles.unitName}>
                                {unit.name}
                              </span>
                              {formatDurationLabel(
                                unit.durationHours,
                                unit.durationMinutes,
                              ) && (
                                <span className={styles.unitMeta}>
                                  (
                                  {formatDurationLabel(
                                    unit.durationHours,
                                    unit.durationMinutes,
                                  )}
                                  )
                                </span>
                              )}
                              {(unit.fileIDs?.length ?? 0) > 0 && (
                                <span className={styles.unitMeta}>
                                  ({unit.fileIDs?.length ?? 0}{" "}
                                  {t("createEducation.units.files")})
                                </span>
                              )}
                            </div>
                            <div className={styles.unitActions}>
                              <Button
                                variant="outlined"
                                color="default"
                                size="small"
                                startIcon={<Edit width={16} height={16} />}
                                onClick={() => handleEditUnit(unit)}
                              >
                                {t("createEducation.units.edit")}
                              </Button>
                              <button
                                className={styles.removeUnitButton}
                                onClick={() => handleRemoveUnit(unit.id)}
                              >
                                <Close width={16} height={16} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* Ünitesiz – tek implicit ünite içeriği inline editör */}
                {formData.educationFormat === "non-unit-based" && (
                  <div className={styles.nonUnitContentCard}>
                    <h3 className={styles.nonUnitContentTitle}>
                      {t("createEducation.nonUnit.title")}
                    </h3>
                    {/* unit-add-modal-content sınıfı, UnitContentEditor'ın
                        nested SCSS kurallarının (objectives/questions vb.)
                        burada da uygulanması için gerekli. */}
                    <div className="unit-add-modal-content">
                      <UnitContentEditor
                        objectives={formData.nonUnitObjectives}
                        onObjectivesChange={(next) =>
                          handleInputChange("nonUnitObjectives", next)
                        }
                        supportedFormats={[
                          "PDF",
                          "DOC",
                          "DOCX",
                          "XLS",
                          "XLSX",
                          "MP4",
                          "WEBM",
                        ]}
                        maxSize={500}
                      />
                    </div>
                  </div>
                )}

                {/* Konum – eğitim oluşturma formunda gösterilmez, planlama aşamasında eklenir */}
                {false && (
                  <div className={styles.formCard}>
                    <div className={styles.cardHeader}>
                      <h2 className={styles.cardTitle}>
                        {t("createEducation.location.title")}
                      </h2>
                    </div>

                    {/* Konum seçimi – tek dropdown: Manuel + Hazır konumlar + Yeni oluştur */}
                    {canViewLocations && (
                      <div className={styles.formField}>
                        <Dropdown
                          label={t("createEducation.location.title")}
                          placeholder={t("createEducation.location.selectPlaceholder")}
                          options={[
                            {
                              label: t("education.location.mode.manual"),
                              value: "__manual__",
                            },
                            ...presetLocationOptions,
                            {
                              label:
                                "+ " +
                                t("createEducation.location.createNew"),
                              value: "__create__",
                            },
                          ]}
                          value={
                            locationMode === "MANUAL"
                              ? "__manual__"
                              : formData.presetLocationId
                          }
                          selectedLabel={
                            locationMode === "MANUAL"
                              ? t("education.location.mode.manual")
                              : presetLocationOptions.find(
                                  (o) =>
                                    o.value === formData.presetLocationId,
                                )?.label ||
                                t("createEducation.location.selectPlaceholder")
                          }
                          onSelect={(value) => {
                            if (value === "__create__") {
                              setIsCreateLocationOpen(true);
                              return;
                            }
                            if (value === "__manual__") {
                              setLocationMode("MANUAL");
                              setFormData((prev) => ({
                                ...prev,
                                presetLocationId: "",
                                location: "",
                                latitude: "",
                                longitude: "",
                              }));
                              return;
                            }
                            const selected = presetLocations.find(
                              (l) => l.id === value,
                            );
                            if (selected) {
                              setLocationMode("PRESET");
                              setFormData((prev) => ({
                                ...prev,
                                presetLocationId: selected.id,
                                location: selected.address ?? "",
                                latitude:
                                  selected.latitude != null
                                    ? String(selected.latitude)
                                    : "",
                                longitude:
                                  selected.longitude != null
                                    ? String(selected.longitude)
                                    : "",
                              }));
                            }
                          }}
                        />
                      </div>
                    )}

                    {/* PRESET: sade adres preview (koordinat yok) */}
                    {locationMode === "PRESET" && isPresetLocationSelected && (
                      <div
                        className={styles.formField}
                        data-testid="preset-location-preview"
                      >
                        <div className={styles.presetPreviewCard}>
                          <span className={styles.presetPreviewLabel}>
                            {t("education.location.preview.address")}
                          </span>
                          <span className={styles.presetPreviewValue}>
                            {formData.location || "—"}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Ulaşım – her iki modda da gösterilir */}
                    <div className={styles.formField}>
                      <Textarea
                        label={t("createEducation.location.transportation")}
                        placeholder={t("createEducation.location.transportationPlaceholder")}
                        value={formData.transportation}
                        maxLength={500}
                        onChange={(e) =>
                          handleInputChange("transportation", e.target.value)
                        }
                        rows={3}
                      />
                    </div>

                    {/* MANUAL: adres input + harita */}
                    {locationMode === "MANUAL" && (
                      <>
                        <div className={styles.formField}>
                          <Input
                            label={t("createEducation.location.address")}
                            placeholder={t("createEducation.location.addressPlaceholder")}
                            value={formData.location}
                            maxLength={500}
                            onChange={(e) =>
                              handleInputChange("location", e.target.value)
                            }
                          />
                        </div>
                        <div className={styles.formField}>
                          <label className={styles.fieldLabel}>
                            {t("createEducation.location.mapPick")}
                          </label>
                          <MapComponent
                            center={
                              formData.latitude && formData.longitude
                                ? {
                                    lat:
                                      Number(formData.latitude) || 41.0082,
                                    lng:
                                      Number(formData.longitude) || 28.9784,
                                  }
                                : { lat: 41.0082, lng: 28.9784 }
                            }
                            zoom={
                              formData.latitude && formData.longitude
                                ? 15
                                : 10
                            }
                            height="320px"
                            onLocationSelect={({ lat, lng, address }) => {
                              setFormData((prev) => ({
                                ...prev,
                                latitude: String(lat),
                                longitude: String(lng),
                                ...(prev.location.trim()
                                  ? {}
                                  : { location: address }),
                              }));
                            }}
                            selectedLocation={
                              formData.latitude && formData.longitude
                                ? {
                                    lat: Number(formData.latitude),
                                    lng: Number(formData.longitude),
                                    address:
                                      formData.location ||
                                      `${formData.latitude}, ${formData.longitude}`,
                                  }
                                : undefined
                            }
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Eğitim Soruları (liveStreamQuestions) ayrı bölüm olarak kaldırıldı */}
              </div>

              {/* Right Section - Sidebar Settings */}
              <div className={styles.rightColumn}>
                {/* Action Buttons */}
                <div className={styles.actionButtons}>
                  {canStartEducation && (
                    <Button
                      variant="contained"
                      color="success"
                      className={styles.publishButton}
                      onClick={() => setIsStartModalOpen(true)}
                      disabled={isStartingEducation}
                    >
                      {isStartingEducation
                        ? t("common.loading")
                        : t("createEducation.actions.startEducation")}
                    </Button>
                  )}
                  {canCompleteEducation && (
                    <Button
                      variant="contained"
                      color="error"
                      className={styles.publishButton}
                      onClick={() => setIsCompleteModalOpen(true)}
                    >
                      {t("createEducation.actions.completeEducation")}
                    </Button>
                  )}
                  {isEditMode &&
                    isEducationManager &&
                    isEducationClosed && (
                      <Button
                        variant="contained"
                        color="secondary"
                        className={styles.publishButton}
                        onClick={() => setIsNewPeriodModalOpen(true)}
                        disabled={isStartingNewPeriod}
                      >
                        {isStartingNewPeriod
                          ? t("common.loading")
                          : t("createEducation.actions.startNewPeriod")}
                      </Button>
                    )}
                  {canSaveAsDraft && (
                    <Button
                      variant="soft"
                      color="primary"
                      className={styles.draftButton}
                      onClick={handleSaveDraft}
                      disabled={isSubmitting || isCoverUploading}
                    >
                      {isSubmitting
                        ? t("common.loading")
                        : t("createEducation.actions.draft")}
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    className={styles.publishButton}
                    onClick={handlePublish}
                    disabled={isSubmitting || isCoverUploading}
                  >
                    {isSubmitting
                      ? t("common.loading")
                      : isAlreadyPublished
                        ? t("createEducation.actions.update")
                        : t("createEducation.actions.publish")}
                  </Button>
                </div>

                {/* Education Status */}
                <div className={styles.sidebarCard}>
                  <div className={styles.sidebarCardContent}>
                    <span className={styles.sidebarCardTitle}>
                      {t("createEducation.sidebar.educationStatus")}
                    </span>
                    {/* Zorunlu Eğitim — işaretlendiğinde hedef kitledeki tüm
                        kullanıcılara backend otomatik atar. Talep toplama
                        toggle'ı devre dışı kalır ve pencere alanları gizlenir. */}
                    <div className={styles.switchWrapper}>
                      <span className={styles.switchLabel}>
                        {t("createEducation.sidebar.mandatory")}
                      </span>
                      <Switch
                        checked={formData.isMandatory}
                        onChange={(checked) => {
                          handleInputChange("isMandatory", checked);
                          if (checked) {
                            // Zorunluya alınınca talep toplama flag + tarihleri sıfırla
                            handleInputChange("isCollectable", false);
                            handleInputChange("requestCollectionDateRange", [
                              null,
                              null,
                            ]);
                          }
                        }}
                      />
                    </div>
                    <div className={styles.switchWrapper}>
                      <span className={styles.switchLabel}>
                        {t("createEducation.sidebar.collectable")}
                      </span>
                      <Switch
                        checked={
                          formData.isMandatory ? false : formData.isCollectable
                        }
                        disabled={formData.isMandatory}
                        onChange={(checked) => {
                          handleInputChange("isCollectable", checked);
                          if (!checked) {
                            handleInputChange("requestCollectionDateRange", [
                              null,
                              null,
                            ]);
                          }
                        }}
                      />
                    </div>
                    {formData.isMandatory && (
                      <p className={styles.mandatoryHint}>
                        {t("createEducation.sidebar.mandatoryHint")}
                      </p>
                    )}
                  </div>
                </div>

                {/* Category */}
                <div className={styles.sidebarCard}>
                  <div className={styles.sidebarCardContent}>
                    <span className={styles.sidebarCardTitle}>
                      {t("createEducation.sidebar.category")}
                    </span>
                    <Dropdown
                      placeholder={t("createEducation.sidebar.categoryPlaceholder")}
                      options={categoryOptions}
                      value={formData.category}
                      allowClear
                      searchable
                      initialDisplayCount={10}
                      selectedLabel={
                        categoryOptions
                          .find((o) => o.value === formData.category)
                          ?.label?.replace(/^[\u00A0]+↳\s*/, "") ||
                        t("createEducation.sidebar.categoryPlaceholder")
                      }
                      onSelect={(value) =>
                        handleInputChange(
                          "category",
                          typeof value === "string" ? value : "",
                        )
                      }
                    />
                  </div>
                </div>

                {/* Instructor */}
                <div className={styles.sidebarCard}>
                  <div className={styles.sidebarCardContent}>
                    <div className={styles.sidebarCardTitleWithIcon}>
                      <User
                        width={20}
                        height={20}
                        className={styles.sidebarCardTitleIcon}
                      />
                      <span className={styles.sidebarCardTitle}>
                        {t("createEducation.sidebar.instructor")}
                      </span>
                    </div>
                    {formData.instructors.length > 0 && (
                      <div className={styles.instructorTags}>
                        {formData.instructors.map((inst) => {
                          const displayName =
                            instructorList.find((i) => i.id === inst.id)
                              ?.name ?? inst.name;
                          return (
                            <span
                              key={inst.id}
                              className={styles.instructorTag}
                            >
                              {displayName}
                              <button
                                type="button"
                                className={styles.instructorTagRemove}
                                onClick={() =>
                                  handleInputChange(
                                    "instructors",
                                    formData.instructors.filter(
                                      (i) => i.id !== inst.id,
                                    ),
                                  )
                                }
                                aria-label={t("common.remove")}
                              >
                                <Close width={12} height={12} />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    <Button
                      variant="outlined"
                      color="default"
                      className={styles.instructorButton}
                      onClick={() => setIsInstructorModalOpen(true)}
                    >
                      {t("createEducation.sidebar.selectInstructor")}
                    </Button>
                  </div>
                </div>

                {/* Eğitim Bazlı Yönetici (Madde 5) */}
                <div className={styles.sidebarCard}>
                  <div className={styles.sidebarCardContent}>
                    <div className={styles.sidebarCardTitleWithIcon}>
                      <User
                        width={20}
                        height={20}
                        className={styles.sidebarCardTitleIcon}
                      />
                      <span className={styles.sidebarCardTitle}>
                        {t("createEducation.sidebar.educationManagers")}
                      </span>
                    </div>
                    {formData.managers.length > 0 && (
                      <div className={styles.instructorTags}>
                        {formData.managers.map((mgr) => (
                          <span
                            key={mgr.id}
                            className={styles.instructorTag}
                          >
                            {mgr.name}
                            <button
                              type="button"
                              className={styles.instructorTagRemove}
                              onClick={() =>
                                handleInputChange(
                                  "managers",
                                  formData.managers.filter(
                                    (m) => m.id !== mgr.id,
                                  ),
                                )
                              }
                              aria-label={t("common.remove")}
                            >
                              <Close width={12} height={12} />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <Dropdown
                      placeholder={
                        isLoadingManagers
                          ? t("common.loading")
                          : t("createEducation.sidebar.selectManager")
                      }
                      // Seçili yöneticiler her zaman options içinde bulunmalı
                      // (BE'den gelen managers liste sonuçlarında olmayabilir).
                      options={(() => {
                        const base = [...managerOptions];
                        formData.managers.forEach((m) => {
                          if (!base.some((o) => o.value === m.id)) {
                            base.unshift({
                              label: m.name,
                              value: m.id,
                              email: m.email,
                            });
                          }
                        });
                        return base;
                      })()}
                      value={formData.managers.map((m) => m.id)}
                      onSelect={handleManagerToggle}
                      onSearch={handleManagerSearch}
                      searchable
                      multiple
                      disabled={isStrictEducationSupervisorUser}
                    />
                  </div>
                </div>

                {/* Capacity */}
                <div className={styles.sidebarCard}>
                  <div className={styles.sidebarCardContent}>
                    <span className={styles.sidebarCardTitle}>
                      {t("createEducation.sidebar.capacity")}
                    </span>
                    <Input
                      type="number"
                      min={0}
                      max={100000}
                      placeholder={t("createEducation.sidebar.capacityPlaceholder")}
                      value={formData.capacity}
                      onChange={(e) =>
                        handleInputChange("capacity", e.target.value)
                      }
                    />
                  </div>
                </div>

                {/* Duration */}
                <div className={styles.sidebarCard}>
                  <div className={styles.sidebarCardContent}>
                    <span className={styles.sidebarCardTitle}>
                      {t("createEducation.sidebar.duration")}
                    </span>
                    <div className={styles.durationRow}>
                      <Input
                        type="number"
                        min={0}
                        placeholder={t("createEducation.sidebar.durationHours")}
                        value={formData.durationHours}
                        onChange={(e) =>
                          handleInputChange("durationHours", e.target.value)
                        }
                      />
                      <Input
                        type="number"
                        min={0}
                        max={59}
                        placeholder={t("createEducation.sidebar.durationMinutes")}
                        value={formData.durationMinutes}
                        onChange={(e) =>
                          handleInputChange("durationMinutes", e.target.value)
                        }
                        error={!!durationError}
                        errorMessage={durationError}
                      />
                    </div>
                  </div>
                </div>

                {/* Budget Requirement */}
                <div className={styles.sidebarCard}>
                  <div className={styles.sidebarCardHeader}>
                    <span className={styles.sidebarCardTitle}>
                      {t("createEducation.sidebar.budgetRequirement")}
                    </span>
                    <Switch
                      checked={formData.hasBudgetRequirement}
                      onChange={(checked) =>
                        handleInputChange("hasBudgetRequirement", checked)
                      }
                      disabled={isBudgetRequirementSwitchDisabled}
                    />
                  </div>
                  {formData.hasBudgetRequirement && (
                    <div className={styles.sidebarCardBody}>
                      <Input
                        placeholder={t("createEducation.sidebar.budgetPlaceholder")}
                        startIcon={<span>₺</span>}
                        inputMode="numeric"
                        value={
                          formData.budget
                            ? new Intl.NumberFormat("tr-TR").format(
                                Number(formData.budget),
                              )
                            : ""
                        }
                        onChange={(e) => {
                          const digits = e.target.value.replace(/\D/g, "");
                          handleInputChange("budget", digits);
                        }}
                        disabled={isBudgetRequirementSwitchDisabled}
                      />
                      {educationIsBudgetApproved ? (
                        <span className={styles.budgetApproved}>
                          Bütçe Onaylandı
                        </span>
                      ) : null}
                      {isEditMode &&
                        educationId &&
                        educationIsWaitingBudgetApproval &&
                        !educationIsBudgetApproved && (
                          <Button
                            type="button"
                            variant="outlined"
                            onClick={handleApproveBudget}
                            disabled={isApprovingBudget}
                          >
                            {isApprovingBudget
                              ? t("common.loading")
                              : t("createEducation.approveBudget")}
                          </Button>
                        )}
                    </div>
                  )}
                </div>

                {/* Exam Definition */}
                <div className={styles.sidebarCard}>
                  <div className={styles.sidebarCardContent}>
                    <span className={styles.sidebarCardTitle}>
                      {t("createEducation.sidebar.examDefinition")}
                    </span>
                    <Dropdown
                      searchable
                      placeholder={t("createEducation.sidebar.examPlaceholder")}
                      options={[
                        {
                          label: t("createEducation.sidebar.examPlaceholder"),
                          value: "",
                        },
                        ...examOptions,
                      ]}
                      value={formData.examDefinition}
                      selectedLabel={
                        examOptions.find(
                          (o) => o.value === formData.examDefinition,
                        )?.label ||
                        t("createEducation.sidebar.examPlaceholder")
                      }
                      onSelect={(value) =>
                        handleInputChange("examDefinition", value)
                      }
                    />
                  </div>
                </div>

                {/* Survey */}
                <div className={styles.sidebarCard}>
                  <div className={styles.sidebarCardContent}>
                    <span className={styles.sidebarCardTitle}>
                      {t("createEducation.sidebar.addSurvey")}
                    </span>
                    <Dropdown
                      searchable
                      placeholder={t("createEducation.sidebar.surveyPlaceholder")}
                      options={[
                        {
                          label: t("createEducation.sidebar.surveyPlaceholder"),
                          value: "",
                        },
                        ...surveyOptions,
                      ]}
                      value={formData.survey}
                      selectedLabel={
                        surveyOptions.find((o) => o.value === formData.survey)
                          ?.label ||
                        t("createEducation.sidebar.surveyPlaceholder")
                      }
                      onSelect={(value) => handleInputChange("survey", value)}
                    />
                  </div>
                </div>

                {/* Certificate */}
                <div className={styles.sidebarCard}>
                  <div className={styles.sidebarCardContent}>
                    <span className={styles.sidebarCardTitle}>
                      {t("createEducation.sidebar.certificate")}
                      <span className={styles.requiredMark}>*</span>
                    </span>
                    <Dropdown
                      searchable
                      placeholder={t("createEducation.sidebar.certificatePlaceholder")}
                      options={[
                        {
                          label: t("createEducation.sidebar.certificatePlaceholder"),
                          value: "",
                        },
                        ...certificateOptions,
                      ]}
                      value={formData.certificate}
                      selectedLabel={
                        certificateOptions.find(
                          (o) => o.value === formData.certificate,
                        )?.label ||
                        t("createEducation.sidebar.certificatePlaceholder")
                      }
                      onSelect={(value) =>
                        handleInputChange("certificate", value)
                      }
                      error={!!fieldErrors.certificate}
                      errorMessage={fieldErrors.certificate}
                    />
                  </div>
                </div>

                {/* Onay Akışı — kayıt talepleri bu akışa girer. Zorunlu eğitimde
                    talep/onay süreci yok, alan da gösterilmez.

                    İki görünüm var: akış tasarımına yetkisi olan (Sistem
                    Yöneticisi) eğitime özel akış seçebilir; yetkisi olmayan
                    (Eğitim Yöneticisi) Sistem Yöneticisinin belirlediği akışı
                    salt-okunur görür. Eskiden ikisine de dropdown çiziliyordu ve
                    yetkisizde liste 403 yüzünden boş kalıyordu. */}
                {!formData.isMandatory && (
                  <div className={styles.sidebarCard}>
                    <div className={styles.sidebarCardContent}>
                      <span className={styles.sidebarCardTitle}>
                        {t("createEducation.sidebar.approvalFlow")}
                      </span>
                      {canChooseApprovalFlow ? (
                        <>
                          <Dropdown
                            searchable
                            placeholder={t("createEducation.sidebar.approvalFlowPlaceholder")}
                            options={[
                              {
                                label: t("createEducation.sidebar.approvalFlowDefault"),
                                value: "",
                              },
                              ...approvalFlowOptions,
                            ]}
                            value={formData.approvalFlow}
                            selectedLabel={
                              approvalFlowOptions.find(
                                (o) => o.value === formData.approvalFlow,
                              )?.label ||
                              t("createEducation.sidebar.approvalFlowDefault")
                            }
                            onSelect={(value) =>
                              handleInputChange("approvalFlow", value)
                            }
                          />
                          {!formData.approvalFlow && systemApprovalFlowLoaded && (
                            <span className={styles.approvalFlowHint}>
                              {systemApprovalFlow
                                ? `Seçim yapılmazsa sistemin onay akışı kullanılır: ${systemApprovalFlow.title}`
                                : "Sistemin onay akışı belirlenmemiş. Onay Akışları ekranından bir akışı sistemin akışı yapın; aksi halde bu eğitim talep toplayamaz."}
                            </span>
                          )}
                        </>
                      ) : (
                        <div className={styles.approvalFlowReadonly}>
                          <span className={styles.approvalFlowReadonlyValue}>
                            {systemApprovalFlowLoaded
                              ? (systemApprovalFlow?.title ?? "Tanımlı değil")
                              : "…"}
                          </span>
                          <span className={styles.approvalFlowHint}>
                            {systemApprovalFlowLoaded && !systemApprovalFlow
                              ? "Sistem Yöneticisi henüz bir onay akışı belirlemedi; bu eğitim talep toplayamaz."
                              : "Onay akışını Sistem Yöneticisi belirler; değiştiğinde tüm yeni eğitimler yeni akıştan yürür."}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Request Collection Date — zorunlu eğitimde gizlenir */}
                {!formData.isMandatory && (
                  <div className={styles.sidebarCard}>
                    <div className={styles.sidebarCardContent}>
                      <span className={styles.sidebarCardTitle}>
                        {t("createEducation.sidebar.requestCollectionDate")}
                        {formData.isCollectable && "*"}
                      </span>
                      <DatePicker
                        placeholder={t("createEducation.sidebar.dateRangePlaceholder")}
                        isRange
                        value={formData.requestCollectionDateRange}
                        onChange={(value) =>
                          handleInputChange("requestCollectionDateRange", value)
                        }
                        allowClear
                        error={!!fieldErrors.requestCollectionDateRange}
                        errorMessage={fieldErrors.requestCollectionDateRange}
                      />
                    </div>
                  </div>
                )}

                {/* Start Date - Eğitim Başlama Tarihi */}
                <div className={styles.sidebarCard}>
                  <div className={styles.sidebarCardContent}>
                    <span className={styles.sidebarCardTitle}>
                      {t("createEducation.sidebar.startDate")}
                    </span>
                    <DatePicker
                      placeholder={t("createEducation.sidebar.datePlaceholder")}
                      value={formData.startDate}
                      onChange={(value) =>
                        handleInputChange("startDate", value)
                      }
                      allowClear
                      error={!!fieldErrors.startDate}
                      errorMessage={fieldErrors.startDate}
                    />
                  </div>
                </div>

                {/* Completion Date */}
                <div className={styles.sidebarCard}>
                  <div className={styles.sidebarCardContent}>
                    <span className={styles.sidebarCardTitle}>
                      {t("createEducation.sidebar.completionDate")}
                    </span>
                    <DatePicker
                      placeholder={t("createEducation.sidebar.datePlaceholder")}
                      value={formData.completionDate}
                      onChange={(value) =>
                        handleInputChange("completionDate", value)
                      }
                      allowClear
                      error={!!fieldErrors.completionDate}
                      errorMessage={fieldErrors.completionDate}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Document Upload Modal */}
      <DocumentUploadModal
        isOpen={isDocumentModalOpen}
        onClose={handleCloseDocumentModal}
        onSubmit={handleAddDocument}
        editingDocument={editingDocument}
        // PPT/PPTX backend allowlist'inde kabul ediliyor; sunum dosyaları da eklenebilmeli.
        supportedFormats={["PDF", "DOC", "DOCX", "XLS", "XLSX", "PPT", "PPTX"]}
        // Backend'in video dışı dosya sınırı 10MB — daha büyüğünü göstermek yanıltıcı.
        maxSize={10}
      />

      <UnitAddModal
        isOpen={isUnitModalOpen}
        onClose={handleCloseUnitModal}
        onSubmit={handleAddUnit}
        editingUnit={editingUnit}
        supportedFormats={[
          "PDF",
          "DOC",
          "DOCX",
          "XLS",
          "XLSX",
          "PPT",
          "PPTX",
          "MP4",
          "WEBM",
        ]}
        maxSize={500}
      />

      {/* Instructor Select Modal */}
      <InstructorSelectModal
        isOpen={isInstructorModalOpen}
        onClose={() => setIsInstructorModalOpen(false)}
        onConfirm={(instructors) =>
          handleInputChange("instructors", instructors)
        }
        selectedInstructors={formData.instructors}
        instructorsList={instructorList}
      />

      {/* Complete Education — full-screen modal with per-user decisions */}
      <CompleteEducationModal
        isOpen={isCompleteModalOpen}
        educationId={educationId}
        onClose={() => setIsCompleteModalOpen(false)}
        onCompleted={handleEducationCompleted}
      />

      {/* Zorunlu eğitim — hedef kitlede olup atanmamış kullanıcılar modalı */}
      <Modal
        isOpen={isMissingUsersOpen}
        onClose={() => setIsMissingUsersOpen(false)}
        title={t("createEducation.mandatory.missingUsersTitle")}
        size="md"
      >
        <div className={styles.missingUsersModal}>
          {isLoadingMissingUsers ? (
            <div className={styles.missingUsersEmpty}>
              {t("common.loading")}
            </div>
          ) : missingUsers.length === 0 ? (
            <div className={styles.missingUsersEmpty}>
              {t("createEducation.mandatory.missingUsersEmpty")}
            </div>
          ) : (
            <div className={styles.missingUsersTableWrapper}>
              <table className={styles.missingUsersTable}>
                <thead>
                  <tr>
                    <th>
                      {t("createEducation.mandatory.missingUsersCol.name")}
                    </th>
                    <th>
                      {t("createEducation.mandatory.missingUsersCol.email")}
                    </th>
                    <th>
                      {t("createEducation.mandatory.missingUsersCol.department")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {missingUsers.map((u) => {
                    const fullName =
                      [u.firstName, u.lastName]
                        .filter((x) => typeof x === "string" && x.trim())
                        .join(" ")
                        .trim() || "—";
                    return (
                      <tr key={u.id}>
                        <td>{fullName}</td>
                        <td>{u.email || "—"}</td>
                        <td>{u.departmentName || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Modal>

      <CreateLocationModal
        isOpen={isCreateLocationOpen}
        onClose={() => setIsCreateLocationOpen(false)}
        onCreated={(loc) => {
          setPresetLocations((prev) => [loc, ...prev]);
          setLocationMode("PRESET");
          setFormData((prev) => ({
            ...prev,
            presetLocationId: loc.id,
            location: loc.address ?? prev.location,
            latitude:
              loc.latitude != null ? String(loc.latitude) : prev.latitude,
            longitude:
              loc.longitude != null ? String(loc.longitude) : prev.longitude,
          }));
        }}
      />

      {/* Start Education Confirmation Modal */}
      <ConfirmModal
        isOpen={isStartModalOpen}
        onClose={() => !isStartingEducation && setIsStartModalOpen(false)}
        onConfirm={handleStartEducation}
        title={t("createEducation.startConfirm.title")}
        message={t("createEducation.startConfirm.message")}
        confirmText={t("createEducation.startConfirm.confirm")}
        cancelText={t("common.cancel")}
        loading={isStartingEducation}
      />

      {/* New Period Confirmation Modal */}
      <ConfirmModal
        isOpen={isNewPeriodModalOpen}
        onClose={() => !isStartingNewPeriod && setIsNewPeriodModalOpen(false)}
        onConfirm={handleStartNewPeriod}
        title={t("createEducation.newPeriodConfirm.title")}
        message={t("createEducation.newPeriodConfirm.message")}
        confirmText={t("createEducation.newPeriodConfirm.confirm")}
        cancelText={t("common.cancel")}
        loading={isStartingNewPeriod}
      />

      {/* Şablon kodundan prefill onay modalı */}
      <ConfirmModal
        isOpen={templatePrefillModalOpen}
        onClose={() => {
          setTemplatePrefillModalOpen(false);
          setTemplatePrefillData(null);
        }}
        onConfirm={handleConfirmTemplatePrefill}
        title={t("createEducation.basicInfo.templatePrefillTitle", {
          defaultValue: "Şablondan doldur",
        })}
        message={t("createEducation.basicInfo.templatePrefillBody", {
          defaultValue:
            "Bu kodla daha önce açılmış bir eğitim bulundu. Form bu eğitimden doldurulsun mu?",
        })}
        confirmText={t("createEducation.basicInfo.templatePrefillConfirm", {
          defaultValue: "Evet, doldur",
        })}
        cancelText={t("common.cancel")}
        loading={isLookingUpTemplate}
      />
    </DashboardLayout>
  );
};

export default CreateEducationPage;
